import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import api, { apiGetMe, apiLogout, clearTokens, apiListDoctors, apiCreateDoctor, apiUpdateDoctor, apiDeleteDoctor, apiBillingStatus, apiCreateCheckout, apiBillingPortal, apiChangePlan, apiCancelSubscription, apiReactivateSubscription, apiRetryPayment, apiListInvoices, apiBuyTopup, apiSubmitSupportTicket, apiUpdateProfile, apiChangePassword, apiGetTeam, apiAddTeamMember, apiUpdateTeamMember, apiRemoveTeamMember, apiGenerateP2PStandalone, apiAppealsDashboard, apiGetSavedTemplates, apiSaveTemplate, apiDeleteSavedTemplate, apiUseSavedTemplate, apiBulkGenerate, apiGetBranding, apiUpdateBranding, apiUploadLogo, apiDeleteLogo, USER_KEY } from './api'
import type { Invoice, SavedTemplateData, BulkLMNItemRequest, BulkLMNResultItem, BrandingData } from './api'
import type { AuthUser, DoctorProfileData, PracticeLocationData, BillingStatus, TeamData, TeamMember, P2PData, AppealsDashboardData } from './api'
import AppealsDashboard from './AppealsDashboard'
import PayerDirectory from './PayerDirectory'
import Logo from './Logo'
import OnboardingChecklist from './components/OnboardingChecklist'
import GuidedTour, { FIRST_LMN_TOUR } from './components/GuidedTour'
import PerformanceBar from './components/PerformanceBar'
import SavedTemplates from './components/SavedTemplates'
import BulkGenerator from './components/BulkGenerator'
import WhatsNext from './components/WhatsNext'
import NotificationCenter from './components/NotificationCenter'
import KeyboardShortcuts from './components/KeyboardShortcuts'
import DoctorSelector from './components/DoctorSelector'
import LMNForm from './components/LMNForm'
import LMNResult from './components/LMNResult'
import SupportTicketModal from './components/SupportTicketModal'
import FirstRunWelcome from './components/FirstRunWelcome'
import BrandingSettings from './components/BrandingSettings'
import { useDebounce } from './hooks/useDebounce'
import { useTemporaryState } from './hooks/useTemporaryState'
import { getApiErrorMessage } from './hooks/useApiError'
import { useToast } from './components/Toast'

// ── Doctor Profile Type ────────────────────────────────────────────────────
interface PracticeLocation {
  label: string         // "Main Office", "Satellite - Plano", etc.
  address: string       // "123 Medical Center Dr, Suite 200, Dallas, TX 75201"
  phone: string         // "(555) 123-4567"
  fax: string           // "(555) 123-4568"
}

interface DoctorProfile {
  id: string
  name: string          // "John Smith, MD"
  npi: string           // 10-digit NPI
  specialty: string     // "Dermatology"
  dea?: string          // DEA number (optional)
  practiceName: string  // "Advanced Dermatology Associates"
  locations: PracticeLocation[]
  // Legacy single-address fields (kept for backward compat with saved data)
  practicePhone?: string
  practiceFax?: string
  practiceAddress?: string
}

/** Migrate old single-address profiles to the new locations array format */
function migrateDoctor(doc: DoctorProfile): DoctorProfile {
  if (doc.locations && doc.locations.length > 0) return doc
  return {
    ...doc,
    locations: [{
      label: 'Main Office',
      address: doc.practiceAddress || '',
      phone: doc.practicePhone || '',
      fax: doc.practiceFax || '',
    }],
  }
}

function getActiveLocation(doc: DoctorProfile, idx: number): PracticeLocation {
  const locs = doc.locations || []
  return locs[idx] || locs[0] || { label: '', address: '', phone: '', fax: '' }
}

interface UserData {
  email: string
  name: string
  plan: string
  loggedIn: boolean
  lmnCount: number
  trialLmnLimit: number
  trialActive: boolean
  billingStarted: boolean
  isAdmin: boolean
  referralCode: string
  referredBy: string | null
  referralCount: number
  referralPaidCount: number
  freeMonthsEarned: number
  freeMonthsUsed: number
  monthlyLmnCount: number
  monthlyLmnLimit: number
  bonusLmnCount: number
  bonusLmnUsed: number
  currentMonth: string
  createdAt: string
  // Production billing state
  paymentFailed: boolean
  cancelAtPeriodEnd: boolean
  subscriptionEndsAt: string | null
  currentPeriodEnd: string | null
}

interface UsageAlert {
  type: string
  remaining: number
  limit: number
  plan: string
  message: string
}

const DEFAULT_USER: UserData = {
  email: '', name: '', plan: 'starter', loggedIn: false, lmnCount: 0,
  trialLmnLimit: 5, trialActive: true, billingStarted: false, isAdmin: false,
  referralCode: '', referredBy: null, referralCount: 0, referralPaidCount: 0,
  freeMonthsEarned: 0, freeMonthsUsed: 0,
  monthlyLmnCount: 0, monthlyLmnLimit: 50, bonusLmnCount: 0, bonusLmnUsed: 0, currentMonth: '',
  createdAt: '',
  paymentFailed: false, cancelAtPeriodEnd: false, subscriptionEndsAt: null, currentPeriodEnd: null,
}

function getUserData(): UserData {
  try {
    const raw = localStorage.getItem(USER_KEY)
    if (!raw) return { ...DEFAULT_USER }
    const data = JSON.parse(raw)
    return {
      email: data.email || '',
      name: data.name || '',
      plan: data.plan || 'starter',
      loggedIn: data.loggedIn || false,
      lmnCount: data.lmnCount || 0,
      trialLmnLimit: data.trialLmnLimit || 5,
      trialActive: data.trialActive !== false,
      billingStarted: data.billingStarted || false,
      isAdmin: data.isAdmin || data.is_admin || false,
      referralCode: data.referralCode || '',
      referredBy: data.referredBy || null,
      referralCount: data.referralCount || 0,
      referralPaidCount: data.referralPaidCount || 0,
      freeMonthsEarned: data.freeMonthsEarned || 0,
      freeMonthsUsed: data.freeMonthsUsed || 0,
      monthlyLmnCount: data.monthlyLmnCount || 0,
      monthlyLmnLimit: data.monthlyLmnLimit || (data.plan === 'professional_plus' ? 300 : data.plan === 'professional' ? 200 : 50),
      bonusLmnCount: data.bonusLmnCount || 0,
      bonusLmnUsed: data.bonusLmnUsed || 0,
      currentMonth: data.currentMonth || '',
      createdAt: data.createdAt || '',
      paymentFailed: data.paymentFailed || false,
      cancelAtPeriodEnd: data.cancelAtPeriodEnd || false,
      subscriptionEndsAt: data.subscriptionEndsAt || null,
      currentPeriodEnd: data.currentPeriodEnd || null,
    }
  } catch {
    return { ...DEFAULT_USER }
  }
}

function updateUserData(updates: Partial<UserData>) {
  const current = getUserData()
  const merged = { ...current, ...updates }
  localStorage.setItem(USER_KEY, JSON.stringify(merged))
}

function getUserPlan(): string {
  return getUserData().plan || 'starter'
}

function getDoctorLimit(): number {
  const plan = getUserPlan()
  if (plan === 'professional' || plan === 'professional_plus') return 8
  if (plan === 'enterprise') return 999
  return 3 // starter
}

// ── Types ──────────────────────────────────────────────────────────────────
interface Citation { number: number; source: string; title: string; url?: string }
interface FillIn   { field: string; placeholder: string; description: string }
interface LMNResult {
  success: boolean
  letter_text: string
  letter_sections: Record<string, string>
  citations: Citation[]
  provider_fill_ins: FillIn[]
  diagnosis_descriptions: string[]
  denial_rebuttal_strategy: string
  confidence_note?: string
  generation_mode: string
}

// ── Shared constants imported from canonical source ─────────────────────
import { CARRIERS, PLAN_TYPES, DENIAL_REASONS, PlanResult, MedicalPolicy } from './components/types'

// ── Shared UI Pieces ──────────────────────────────────────────────────────
const Label = ({ children }: { children: React.ReactNode }) => (
  <label className="block text-[13px] font-semibold text-slate-700 dark:text-slate-200 mb-1.5">{children}</label>
)
const InputClass = "w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 bg-white transition-all placeholder:text-slate-300 dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder:text-slate-400"
const Badge = ({ children, color }: { children: React.ReactNode; color: string }) => (
  <span className={`inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full ${color}`}>{children}</span>
)

// ── ICD-10 Search Result Type ─────────────────────────────────────────────
interface ICD10Result {
  code: string
  description: string
  body_system: string
}

// ── Text highlight helper ─────────────────────────────────────────────────
function HighlightMatch({ text, query }: { text: string; query: string }) {
  if (!query || query.length < 1) return <>{text}</>
  const idx = text.toLowerCase().indexOf(query.toLowerCase())
  if (idx === -1) return <>{text}</>
  return (
    <>
      {text.slice(0, idx)}
      <span className="bg-amber-200/60 text-amber-900 font-bold rounded-sm px-0.5">{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </>
  )
}

// ── ICD-10 Autocomplete Component ─────────────────────────────────────────
function ICD10Autocomplete({
  selectedCodes,
  onAdd,
  onRemove,
}: {
  selectedCodes: ICD10Result[]
  onAdd: (item: ICD10Result) => void
  onRemove: (code: string) => void
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<ICD10Result[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [highlightIdx, setHighlightIdx] = useState(0)
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [showManualEntry, setShowManualEntry] = useState(false)
  const [manualCode, setManualCode] = useState('')
  const [manualDesc, setManualDesc] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitMsg, setSubmitMsg] = useState('')
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const doSearch = useCallback(async (q: string) => {
    if (q.length < 1) { setResults([]); setIsOpen(false); setHasSearched(false); return }
    setLoading(true)
    setHasSearched(true)
    try {
      const res = await axios.get<{ results: ICD10Result[] }>('/api/icd10/search', { params: { q } })
      const filtered = res.data.results.filter(r => !selectedCodes.some(s => s.code === r.code))
      setResults(filtered)
      setIsOpen(true)
      setHighlightIdx(0)
    } catch { setResults([]) }
    finally { setLoading(false) }
  }, [selectedCodes])

  const debouncedSearch = useDebounce(doSearch, 120)

  const handleInput = (val: string) => {
    setQuery(val)
    debouncedSearch(val)
  }

  const handleSelect = (item: ICD10Result) => {
    onAdd(item)
    setQuery('')
    setResults([])
    setIsOpen(false)
    setHasSearched(false)
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIdx(i => Math.min(i + 1, results.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIdx(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter' && results[highlightIdx]) { e.preventDefault(); handleSelect(results[highlightIdx]) }
    else if (e.key === 'Escape') { setIsOpen(false) }
  }

  // Auto-show popular codes on focus when empty
  const handleFocus = () => {
    if (query.length >= 1 && results.length > 0) { setIsOpen(true); return }
    if (query.length === 0 && selectedCodes.length === 0) {
      // Show popular codes hint
      doSearch('diabetes') // pre-populate with common search
      .then(() => {}) // will open dropdown
    }
  }

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div ref={wrapperRef} className="relative">
      {selectedCodes.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedCodes.map((item, i) => (
            <div key={item.code}
              className={`flex items-center gap-1.5 text-xs rounded-lg px-2.5 py-1.5 border font-medium
                ${i === 0
                  ? 'bg-teal-50 border-teal-300 text-teal-700'
                  : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
              <span className="font-mono font-bold">{item.code}</span>
              <span className="opacity-40">|</span>
              <span className="max-w-[180px] truncate">{item.description}</span>
              {i === 0 && <span className="text-[10px] font-bold bg-teal-500 text-white rounded px-1.5 py-0.5 ml-0.5 uppercase tracking-wider">Primary</span>}
              <button onClick={() => onRemove(item.code)}
                className="ml-1 text-slate-400 hover:text-red-500 font-bold leading-none transition-colors">&times;</button>
            </div>
          ))}
        </div>
      )}

      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        </div>
        <input
          ref={inputRef}
          className={InputClass + ' !pl-9'}
          value={query}
          onChange={e => handleInput(e.target.value)}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={selectedCodes.length > 0 ? "Add another code..." : "Type code or diagnosis (e.g. L40 or psoriasis)"}
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-slate-200 border-t-teal-500 rounded-full animate-spin" />
          </div>
        )}
      </div>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl shadow-slate-200/50 max-h-64 overflow-y-auto">
          {results.length > 0 ? (
            <>
              <div className="px-3.5 py-1.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">
                  {results.length} match{results.length !== 1 ? 'es' : ''} found
                </span>
                <span className="text-[10px] text-slate-300">↑↓ navigate · Enter select · Esc close</span>
              </div>
              {results.map((item, idx) => (
                <button
                  key={item.code}
                  className={`w-full text-left px-3.5 py-2.5 flex items-start gap-3 border-b border-slate-50 last:border-0 transition-colors
                    ${idx === highlightIdx ? 'bg-teal-50 border-l-2 border-l-teal-500' : 'hover:bg-slate-50 border-l-2 border-l-transparent'}`}
                  onMouseEnter={() => setHighlightIdx(idx)}
                  onClick={() => handleSelect(item)}
                >
                  <span className={`font-mono font-bold text-sm whitespace-nowrap mt-0.5 ${idx === highlightIdx ? 'text-teal-700' : 'text-teal-600'}`}>
                    <HighlightMatch text={item.code} query={query} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-700 leading-snug"><HighlightMatch text={item.description} query={query} /></p>
                    <p className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-wide">{item.body_system}</p>
                  </div>
                  {idx === highlightIdx && (
                    <span className="text-[10px] text-teal-500 font-medium whitespace-nowrap mt-1">Enter ↵</span>
                  )}
                </button>
              ))}
            </>
          ) : hasSearched && !loading && results.length === 0 ? (
            <div className="px-4 py-4">
              <p className="text-sm text-slate-500 font-medium">No ICD-10 codes found for &ldquo;{query}&rdquo;</p>
              {!showManualEntry ? (
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowManualEntry(true) }}
                  onMouseDown={(e) => e.stopPropagation()}
                  data-testid="manual-icd10-entry"
                  className="mt-2 text-sm text-teal-600 hover:text-teal-700 font-semibold flex items-center gap-1"
                >
                  <span>+</span> Enter code manually
                </button>
              ) : (
                <div className="mt-3 space-y-2 border-t border-slate-100 pt-3" onMouseDown={e => e.stopPropagation()}>
                  <p className="text-[11px] text-slate-400 font-medium uppercase tracking-wider">Submit a new ICD-10 code</p>
                  <input
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                    placeholder="ICD-10 Code (e.g. M79.3)"
                    value={manualCode}
                    onChange={e => setManualCode(e.target.value.toUpperCase())}
                  />
                  <input
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                    placeholder="Description (e.g. Panniculitis, unspecified)"
                    value={manualDesc}
                    onChange={e => setManualDesc(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={async (e) => {
                        e.preventDefault()
                        if (!manualCode || !manualDesc) return
                        setSubmitting(true)
                        try {
                          const res = await api.post('/api/icd10/submit-custom', { code: manualCode, description: manualDesc })
                          const data = res.data
                          if (data.status === 'verified' || data.status === 'already_exists' || data.status === 'already_verified') {
                            // Immediately usable — add it
                            onAdd({ code: manualCode, description: manualDesc, body_system: 'User Submitted' })
                            setQuery('')
                            setIsOpen(false)
                            setShowManualEntry(false)
                            setManualCode('')
                            setManualDesc('')
                            setSubmitMsg('')
                          } else {
                            setSubmitMsg(data.message)
                            // Still add it locally for this session
                            onAdd({ code: manualCode, description: manualDesc, body_system: 'Pending Verification' })
                            setQuery('')
                            setTimeout(() => { setIsOpen(false); setShowManualEntry(false); setManualCode(''); setManualDesc(''); setSubmitMsg('') }, 2000)
                          }
                        } catch (err: any) {
                          setSubmitMsg(err?.response?.data?.detail || 'Error submitting code')
                        }
                        setSubmitting(false)
                      }}
                      disabled={!manualCode || !manualDesc || submitting}
                      className="flex-1 px-3 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {submitting ? 'Submitting...' : 'Submit Code'}
                    </button>
                    <button
                      onClick={(e) => { e.preventDefault(); setShowManualEntry(false); setManualCode(''); setManualDesc('') }}
                      className="px-3 py-2 text-sm text-slate-500 hover:text-slate-700 font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                  {submitMsg && <p className="text-xs text-teal-600 font-medium">{submitMsg}</p>}
                  <p className="text-[10px] text-slate-400">Once verified, this code will be available for all RxCheckUp users.</p>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

// ── Drug Search Result Type ───────────────────────────────────────────────
interface DrugResult {
  display: string
  brand_name: string
  generic_name: string
  drug_class: string
  all_brands: string[]
  fda_indications?: string[]
  diagnosis_match?: boolean
  matched_icd10?: string[]
}

// ── Drug Autocomplete Component ──────────────────────────────────────────
function DrugAutocomplete({
  value,
  onChange,
  icd10Codes,
}: {
  value: string
  onChange: (val: string) => void
  icd10Codes: string[]
}) {
  const [query, setQuery] = useState(value)
  const [results, setResults] = useState<DrugResult[]>([])
  const [diagnosisDrugs, setDiagnosisDrugs] = useState<DrugResult[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [highlightIdx, setHighlightIdx] = useState(0)
  const [loading, setLoading] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const prevCodesRef = useRef('')

  // Fetch diagnosis-matched drugs when ICD-10 codes change
  useEffect(() => {
    const codesKey = icd10Codes.join(',')
    if (codesKey === prevCodesRef.current) return
    prevCodesRef.current = codesKey
    if (icd10Codes.length === 0) { setDiagnosisDrugs([]); return }
    axios.get<{ diagnosis_matched: DrugResult[] }>('/api/drugs/search', { params: { q: '', icd10_codes: codesKey } })
      .then(res => setDiagnosisDrugs(res.data.diagnosis_matched || []))
      .catch(() => setDiagnosisDrugs([]))
  }, [icd10Codes])

  const [hasSearched, setHasSearched] = useState(false)

  const doSearch = useCallback(async (q: string) => {
    if (q.length < 1) { setResults([]); setIsOpen(false); setHasSearched(false); return }
    setLoading(true)
    setHasSearched(true)
    try {
      const params: Record<string, string> = { q }
      if (icd10Codes.length > 0) params.icd10_codes = icd10Codes.join(',')
      const res = await axios.get<{ results: DrugResult[] }>('/api/drugs/search', { params })
      setResults(res.data.results)
      setIsOpen(true)
      setHighlightIdx(0)
    } catch { setResults([]) }
    finally { setLoading(false) }
  }, [icd10Codes])

  const debouncedSearch = useDebounce(doSearch, 120)

  const handleInput = (val: string) => {
    setQuery(val)
    onChange(val)
    debouncedSearch(val)
  }

  const handleSelect = (item: DrugResult) => {
    setQuery(item.display)
    onChange(item.display)
    setResults([])
    setIsOpen(false)
    setHasSearched(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIdx(i => Math.min(i + 1, results.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIdx(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter' && results[highlightIdx]) { e.preventDefault(); handleSelect(results[highlightIdx]) }
    else if (e.key === 'Escape') { setIsOpen(false) }
  }

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => { setQuery(value) }, [value])

  return (
    <div ref={wrapperRef} className="relative">
      {/* Diagnosis-matched drug suggestions */}
      {diagnosisDrugs.length > 0 && !value && (
        <div className="mb-2 border border-teal-200 rounded-xl overflow-hidden">
          <div className="px-3.5 py-2 bg-teal-50 border-b border-teal-200 flex items-center gap-2">
            <span className="text-teal-500 text-xs">&#9883;</span>
            <span className="text-[10px] font-bold text-teal-600 uppercase tracking-wider">Indicated for this diagnosis — click to select</span>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {diagnosisDrugs.map(drug => (
              <button key={drug.display} onClick={() => handleSelect(drug)}
                className="w-full text-left px-3.5 py-2.5 flex items-start gap-2.5 border-b border-slate-50 last:border-0 hover:bg-teal-50/50 transition-colors">
                <span className="w-5 h-5 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center flex-shrink-0 text-[10px] font-bold mt-0.5">Rx</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-700 font-semibold">{drug.brand_name}</span>
                    <span className="text-xs text-slate-400">({drug.generic_name})</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded-full font-medium">{drug.drug_class}</span>
                    {drug.fda_indications && drug.fda_indications.length > 0 && (
                      <span className="text-[10px] text-slate-400 truncate">{drug.fda_indications[0]}</span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        </div>
        <input
          id="drug-search-input"
          ref={inputRef}
          className={InputClass + ' !pl-9'}
          value={query}
          onChange={e => handleInput(e.target.value)}
          onFocus={() => { if (query.length >= 1) doSearch(query) }}
          onKeyDown={handleKeyDown}
          placeholder={diagnosisDrugs.length > 0 ? "Select above or type to search..." : "Type brand or generic name (e.g. Humira, semaglutide)"}
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-slate-200 border-t-teal-500 rounded-full animate-spin" />
          </div>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl shadow-slate-200/50 max-h-64 overflow-y-auto">
          {results.length > 0 ? (
            <>
              <div className="px-3.5 py-1.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">
                  {results.length} drug{results.length !== 1 ? 's' : ''} found
                </span>
                <span className="text-[10px] text-slate-300">↑↓ navigate · Enter select</span>
              </div>
              {results.map((item, idx) => (
                <button
                  key={item.display}
                  className={`w-full text-left px-3.5 py-2.5 flex items-start gap-3 border-b border-slate-50 last:border-0 transition-colors
                    ${idx === highlightIdx ? 'bg-teal-50 border-l-2 border-l-teal-500' : 'hover:bg-slate-50 border-l-2 border-l-transparent'}`}
                  onMouseEnter={() => setHighlightIdx(idx)}
                  onClick={() => handleSelect(item)}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-700 font-semibold leading-snug">
                        <HighlightMatch text={item.brand_name} query={query} />
                      </span>
                      <span className="text-xs text-slate-400">
                        (<HighlightMatch text={item.generic_name} query={query} />)
                      </span>
                    </div>
                    {item.diagnosis_match && (
                      <span className="text-[10px] text-teal-600 font-medium">✓ Matches your diagnosis</span>
                    )}
                  </div>
                  <span className="text-[10px] text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full whitespace-nowrap mt-1 font-medium">{item.drug_class}</span>
                  {idx === highlightIdx && (
                    <span className="text-[10px] text-teal-500 font-medium whitespace-nowrap mt-1">Enter ↵</span>
                  )}
                </button>
              ))}
            </>
          ) : hasSearched && !loading ? (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-slate-500 font-medium">No drugs found for &ldquo;{query}&rdquo;</p>
              <p className="text-[11px] text-slate-400 mt-1">Try a brand name (e.g. Humira) or generic name (e.g. adalimumab)</p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

// ── Prior Treatment Suggestion Type ──────────────────────────────────────
interface PriorTxSuggestion {
  name: string
  category: string
  note: string
}

// ── Prior Treatments Component ───────────────────────────────────────────
function PriorTreatments({
  selectedTreatments,
  onAdd,
  onRemove,
  deniedProduct,
  icd10Codes,
  policyRequiredTreatments,
}: {
  selectedTreatments: string[]
  onAdd: (name: string) => void
  onRemove: (name: string) => void
  deniedProduct: string
  icd10Codes: string[]
  policyRequiredTreatments?: string[]
}) {
  const [suggestions, setSuggestions] = useState<PriorTxSuggestion[]>([])
  const [customInput, setCustomInput] = useState('')
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  const prevRequestRef = useRef('')

  useEffect(() => {
    const requestKey = `${deniedProduct}|${icd10Codes.join(',')}`
    if (requestKey === prevRequestRef.current) return
    if (!deniedProduct && icd10Codes.length === 0) { setSuggestions([]); return }
    prevRequestRef.current = requestKey

    const fetchSuggestions = async () => {
      setLoadingSuggestions(true)
      try {
        const res = await axios.get<{ suggestions: PriorTxSuggestion[] }>('/api/prior-treatments/suggest', {
          params: { denied_product: deniedProduct, icd10_codes: icd10Codes.join(',') }
        })
        setSuggestions(res.data.suggestions)
      } catch { setSuggestions([]) }
      finally { setLoadingSuggestions(false) }
    }
    fetchSuggestions()
  }, [deniedProduct, icd10Codes])

  const handleAddCustom = () => {
    const val = customInput.trim()
    if (val && !selectedTreatments.includes(val)) {
      onAdd(val)
      setCustomInput('')
    }
  }

  const availableSuggestions = suggestions.filter(s => !selectedTreatments.includes(s.name))

  return (
    <div>
      {selectedTreatments.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedTreatments.map(tx => (
            <div key={tx}
              className="flex items-center gap-1.5 text-xs rounded-lg px-2.5 py-1.5 bg-slate-50 border border-slate-200 text-slate-600 font-medium">
              <span>{tx}</span>
              <button onClick={() => onRemove(tx)}
                className="ml-0.5 text-slate-400 hover:text-red-500 font-bold leading-none transition-colors">&times;</button>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <input
          className={InputClass}
          value={customInput}
          onChange={e => setCustomInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustom() } }}
          placeholder="Type a treatment name or select below"
        />
        {customInput.trim() && (
          <button onClick={handleAddCustom}
            className="px-3.5 py-2 text-xs font-semibold rounded-xl bg-teal-500 text-white hover:bg-teal-600 transition-colors whitespace-nowrap">
            + Add
          </button>
        )}
      </div>

      {policyRequiredTreatments && policyRequiredTreatments.length > 0 && (
        <div className="mt-2.5 border border-teal-200 rounded-xl overflow-hidden">
          <div className="px-3.5 py-2 bg-teal-500 flex items-center gap-2">
            <span className="text-white text-xs">&#9989;</span>
            <span className="text-[10px] font-bold text-white uppercase tracking-wider">Required by payer policy</span>
          </div>
          <div className="max-h-40 overflow-y-auto bg-teal-50/30">
            {policyRequiredTreatments
              .filter(tx => !selectedTreatments.some(s => tx.toLowerCase().includes(s.toLowerCase().split(' ')[0]) || s.toLowerCase().includes(tx.toLowerCase().split(' ')[0])))
              .map(tx => (
              <button key={tx} onClick={() => onAdd(tx)}
                className="w-full text-left px-3.5 py-2.5 flex items-center gap-2.5 border-b border-teal-100/50 last:border-0 hover:bg-teal-50 transition-colors">
                <span className="w-5 h-5 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">+</span>
                <span className="text-sm text-slate-700 font-medium">{tx}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {loadingSuggestions && (
        <div className="flex items-center gap-2 mt-2.5 text-xs text-slate-400">
          <div className="w-3 h-3 border-2 border-slate-200 border-t-teal-500 rounded-full animate-spin" />
          Loading suggestions...
        </div>
      )}

      {availableSuggestions.length > 0 && (
        <div className="mt-2.5 border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-3.5 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
            <span className="text-slate-400 text-xs">&#128138;</span>
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Common prior treatments</span>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {availableSuggestions.map(s => (
              <button key={s.name} onClick={() => onAdd(s.name)}
                className="w-full text-left px-3.5 py-2.5 flex items-start gap-2.5 border-b border-slate-50 last:border-0 hover:bg-slate-50/80 transition-colors">
                <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0 text-xs mt-0.5">+</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-700 font-medium">{s.name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-400 font-medium">{s.category}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">{s.note}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Plan Search Result Type ───────────────────────────────────────────────
// PlanResult and MedicalPolicy imported from components/types.tsx

// ── Plan Selector Component ──────────────────────────────────────────────
function PlanSelector({
  carrier,
  planType,
  selectedPlan,
  onSelect,
  onAddPlan,
}: {
  carrier: string
  planType: string
  selectedPlan: PlanResult | null
  onSelect: (plan: PlanResult | null) => void
  onAddPlan: () => void
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<PlanResult[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [highlightIdx, setHighlightIdx] = useState(0)
  const [loading, setLoading] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!carrier) { setResults([]); onSelect(null); return }
    setLoading(true)
    axios.get<{ results: PlanResult[] }>('/api/plans/search', { params: { carrier } })
      .then(res => setResults(res.data.results))
      .catch(() => setResults([]))
      .finally(() => setLoading(false))
    onSelect(null)
    setQuery('')
  }, [carrier])

  // Filter by plan type first, then by search query
  const typeFilteredResults = (() => {
    if (!planType) return results
    const ptLower = planType.toLowerCase()
    return results.filter(r => {
      const cat = (r.plan_category || '').toLowerCase()
      const name = (r.plan_name || '').toLowerCase()
      const detail = (r.plan_type_detail || '').toLowerCase()

      // Commercial: HMO, PPO, EPO, POS, HDHP, Indemnity — exclude Medicare/Medicaid/Marketplace
      if (ptLower === 'commercial') return cat === 'commercial' || (cat !== 'medicare' && cat !== 'medicaid' && cat !== 'marketplace' && !name.includes('medicare') && !name.includes('medicaid') && !name.includes('tricare') && !name.includes('workers'))

      // Medicare Advantage: MAPD, MA-only, Medicare HMO/PPO
      if (ptLower === 'medicare_advantage') return cat === 'medicare' || detail === 'mapd' || detail === 'ma' || name.includes('medicare advantage') || (name.includes('medicare') && (detail === 'hmo' || detail === 'ppo'))

      // Medicare Part D: PDP (Prescription Drug Plans) and standalone MAPD
      if (ptLower === 'medicare_part_d') return detail === 'pdp' || detail === 'mapd' || name.includes('part d') || name.includes('pdp') || (cat === 'medicare' && (detail === 'pdp' || name.includes('prescription drug')))

      // Medicaid Managed Care: Medicaid MCO plans
      if (ptLower === 'medicaid_managed') return cat === 'medicaid' || name.includes('medicaid')

      // Managed Medicaid (MCO): same as medicaid_managed
      if (ptLower === 'managed_medicaid') return cat === 'medicaid' || name.includes('medicaid') || name.includes('mco')

      // Self-Insured / ERISA: uses commercial formularies
      if (ptLower === 'self_insured') return cat === 'commercial' || name.includes('self-insured') || name.includes('erisa') || name.includes('self insured')

      // ACA Marketplace / Exchange plans
      if (ptLower === 'exchange_marketplace') return cat === 'marketplace' || name.includes('marketplace') || name.includes('exchange') || name.includes('aca') || name.includes('obamacare')

      // Federal Employee (FEHB)
      if (ptLower === 'federal_employee') return name.includes('fehb') || name.includes('federal') || name.includes('geha') || name.includes('bcbs federal')

      // State Employee Plans
      if (ptLower === 'state_employee') return name.includes('state employee') || name.includes('calpers') || name.includes('state health')

      // Union / Multiemployer
      if (ptLower === 'union_multiemployer') return name.includes('union') || name.includes('multiemployer') || name.includes('taft-hartley') || name.includes('taft hartley')

      // TRICARE (Military)
      if (ptLower === 'tricare') return cat === 'tricare' || name.includes('tricare') || name.includes('military')

      // Workers' Compensation
      if (ptLower === 'workers_comp') return cat === 'workers comp' || name.includes('workers') || name.includes('work comp') || name.includes('wc ')

      return true
    })
  })()

  const filteredResults = query
    ? typeFilteredResults.filter(r => r.plan_name.toLowerCase().includes(query.toLowerCase()))
    : typeFilteredResults

  const handleInput = (val: string) => {
    setQuery(val)
    setIsOpen(true)
    setHighlightIdx(0)
    if (!val.trim()) onSelect(null)
  }

  const handleSelect = (plan: PlanResult) => {
    onSelect(plan)
    setQuery(plan.plan_name)
    setIsOpen(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || filteredResults.length === 0) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIdx(i => Math.min(i + 1, filteredResults.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIdx(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter' && filteredResults[highlightIdx]) { e.preventDefault(); handleSelect(filteredResults[highlightIdx]) }
    else if (e.key === 'Escape') { setIsOpen(false) }
  }

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  if (!carrier) return null

  return (
    <div ref={wrapperRef} className="relative">
      {selectedPlan && (
        <div className="space-y-1.5 mb-2">
          <div className={`flex-1 text-xs rounded-xl px-3 py-2 flex items-center gap-2 font-medium ${
            selectedPlan.unverified
              ? 'bg-amber-50 border border-amber-200 text-amber-700'
              : 'bg-emerald-50 border border-emerald-200 text-emerald-700'
          }`}>
            <span className="font-semibold">{selectedPlan.plan_name}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
              selectedPlan.unverified ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100'
            }`}>{selectedPlan.plan_category}</span>
            {selectedPlan.unverified && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-700 font-bold">UNVERIFIED</span>
            )}
            {selectedPlan.contract_id && (
              <span className="text-[10px] opacity-60 font-mono">ID: {selectedPlan.contract_id}-{selectedPlan.plan_id}</span>
            )}
            <button onClick={() => { onSelect(null); setQuery('') }}
              className="ml-auto text-slate-400 hover:text-red-500 font-bold transition-colors">&times;</button>
          </div>
          {selectedPlan.unverified && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-amber-50/60 border border-amber-100 text-[11px] text-amber-600">
              <span className="text-amber-500 text-sm leading-none mt-0.5">⚠</span>
              <span>This plan was manually added. Formulary data may be incomplete or unverified, which could affect LMN accuracy. Upload the plan's formulary for verified results.</span>
            </div>
          )}
        </div>
      )}

      {!selectedPlan && (
        <div className="relative">
          <input
            className={InputClass}
            value={query}
            onChange={e => handleInput(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={loading ? "Loading plans..." : `Search ${carrier} plans...`}
          />
          {loading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-slate-200 border-t-teal-500 rounded-full animate-spin" />
            </div>
          )}
        </div>
      )}

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl shadow-slate-200/50 max-h-[420px] overflow-y-auto">
          {filteredResults.length > 0 ? (
            <>
              {/* Group results by category */}
              {(() => {
                const grouped: Record<string, typeof filteredResults> = {}
                filteredResults.slice(0, 50).forEach(plan => {
                  const cat = plan.plan_category || 'Other'
                  if (!grouped[cat]) grouped[cat] = []
                  grouped[cat].push(plan)
                })
                let globalIdx = 0
                return Object.entries(grouped).map(([category, plans]) => (
                  <div key={category}>
                    <div className="sticky top-0 z-10 px-3.5 py-1.5 bg-slate-50 border-b border-slate-100">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{category} Plans ({plans.length})</span>
                    </div>
                    {plans.map((plan) => {
                      const myIdx = globalIdx++
                      return (
                        <button
                          key={`${plan.plan_name}-${plan.contract_id}-${plan.plan_id}`}
                          className={`w-full text-left px-3.5 py-2 flex items-center gap-2 border-b border-slate-50 last:border-0 transition-colors
                            ${myIdx === highlightIdx ? 'bg-teal-50/50' : 'hover:bg-slate-50'}`}
                          onMouseEnter={() => setHighlightIdx(myIdx)}
                          onClick={() => handleSelect(plan)}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-[13px] text-slate-700 leading-snug">{plan.plan_name}</p>
                          </div>
                          {plan.unverified && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-600 font-bold whitespace-nowrap flex-shrink-0">⚠ Unverified</span>
                          )}
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium whitespace-nowrap flex-shrink-0">{plan.plan_type_detail || category}</span>
                        </button>
                      )
                    })}
                  </div>
                ))
              })()}
            </>
          ) : query ? (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-slate-400">No plans match "{query}"</p>
            </div>
          ) : null}

          {/* "Can't find your plan?" footer */}
          <div className="sticky bottom-0 z-10 border-t border-slate-200 bg-gradient-to-t from-white via-white to-white/95">
            <button
              onClick={(e) => { e.stopPropagation(); setIsOpen(false); onAddPlan() }}
              className="w-full px-3.5 py-2.5 text-left flex items-center gap-2.5 hover:bg-amber-50/50 transition-colors"
            >
              <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-xs font-bold flex-shrink-0">+</span>
              <div>
                <p className="text-[13px] text-amber-700 font-semibold">Can't find your plan?</p>
                <p className="text-[10px] text-slate-400">Add it manually and upload the formulary</p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Doctor Form Component ────────────────────────────────────────────────
function DoctorForm({
  doctor, onSave, onCancel, isAtLimit, planName, limit,
}: {
  doctor: DoctorProfile | null
  onSave: (d: DoctorProfile) => void
  onCancel: () => void
  isAtLimit: boolean
  planName: string
  limit: number
}) {
  const migrated = doctor ? migrateDoctor(doctor) : null
  const [form, setForm] = useState({
    id: migrated?.id || crypto.randomUUID(),
    name: migrated?.name || '',
    npi: migrated?.npi || '',
    specialty: migrated?.specialty || '',
    dea: migrated?.dea || '',
    practiceName: migrated?.practiceName || '',
  })
  const [locations, setLocations] = useState<PracticeLocation[]>(
    migrated?.locations && migrated.locations.length > 0
      ? migrated.locations
      : [{ label: 'Main Office', address: '', phone: '', fax: '' }]
  )
  const [errors, setErrors] = useState<Record<string, string>>({})

  const updateForm = (field: string, value: string) =>
    setForm(f => ({ ...f, [field]: value }))

  const updateLocation = (idx: number, field: keyof PracticeLocation, value: string) =>
    setLocations(locs => locs.map((l, i) => i === idx ? { ...l, [field]: value } : l))

  const addLocation = () =>
    setLocations(locs => [...locs, { label: `Location ${locs.length + 1}`, address: '', phone: '', fax: '' }])

  const removeLocation = (idx: number) =>
    setLocations(locs => locs.filter((_, i) => i !== idx))

  const validate = () => {
    const errs: Record<string, string> = {}
    if (!form.name.trim()) errs.name = 'Required'
    if (!form.npi.trim()) errs.npi = 'Required'
    else if (!/^\d{10}$/.test(form.npi.replace(/\D/g, ''))) errs.npi = 'Must be 10 digits'
    if (!form.specialty) errs.specialty = 'Required'
    if (!form.practiceName.trim()) errs.practiceName = 'Required'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    onSave({
      id: form.id,
      name: form.name,
      npi: form.npi.replace(/\D/g, ''),
      specialty: form.specialty,
      dea: form.dea || undefined,
      practiceName: form.practiceName,
      locations: locations.filter(l => l.label || l.address || l.phone || l.fax),
    })
  }

  if (isAtLimit) {
    return (
      <div className="text-center py-6">
        <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4 text-2xl">
          &#128274;
        </div>
        <h3 className="text-lg font-bold text-slate-800">Doctor Limit Reached</h3>
        <p className="text-sm text-slate-500 mt-2">
          Your <span className="font-semibold capitalize">{planName}</span> plan supports up to {limit} doctor profiles.
        </p>
        <p className="text-sm text-slate-500 mt-1">
          {planName === 'starter'
            ? 'Upgrade to Professional for up to 8 doctors.'
            : planName === 'professional' || planName === 'professional_plus'
            ? 'Contact us about Enterprise plans for unlimited profiles.'
            : 'Contact us about enterprise plans for unlimited profiles.'}
        </p>
        <div className="flex gap-3 mt-6">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl font-semibold text-sm border border-slate-200 text-slate-500 hover:bg-slate-50">
            Close
          </button>
          <button onClick={() => { onCancel() }}
            className="flex-1 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-md">
            Upgrade Plan
          </button>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[80vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-1 sticky top-0 bg-white pb-2 z-10">
        <h3 className="text-base font-bold text-slate-800">{doctor ? 'Edit Doctor Profile' : 'Add Doctor Profile'}</h3>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600 text-lg font-bold">&times;</button>
      </div>
      <p className="text-[11px] text-slate-400 -mt-3">This info auto-fills into every LMN generated under this doctor.</p>

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label>Full Name & Credentials <span className="text-rose-400">*</span></Label>
          <input className={InputClass} value={form.name} onChange={e => updateForm('name', e.target.value)}
            placeholder="e.g. John Smith, MD" />
          {errors.name && <p className="text-[10px] text-red-500 mt-0.5">{errors.name}</p>}
        </div>
        <div>
          <Label>NPI Number <span className="text-rose-400">*</span></Label>
          <input className={InputClass} value={form.npi} onChange={e => updateForm('npi', e.target.value)}
            placeholder="10-digit NPI" maxLength={10} />
          {errors.npi && <p className="text-[10px] text-red-500 mt-0.5">{errors.npi}</p>}
        </div>
        <div>
          <Label>Specialty <span className="text-rose-400">*</span></Label>
          <select className={InputClass} value={form.specialty} onChange={e => updateForm('specialty', e.target.value)}>
            <option value="">Select...</option>
            {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {errors.specialty && <p className="text-[10px] text-red-500 mt-0.5">{errors.specialty}</p>}
        </div>
        <div className="col-span-2">
          <Label>DEA Number <span className="text-slate-300 font-normal">(Optional)</span></Label>
          <input className={InputClass} value={form.dea || ''} onChange={e => updateForm('dea', e.target.value)}
            placeholder="e.g. AB1234567" />
        </div>
      </div>

      <div className="border-t border-slate-100 pt-4 space-y-3">
        <div>
          <Label>Practice Name <span className="text-rose-400">*</span></Label>
          <input className={InputClass} value={form.practiceName} onChange={e => updateForm('practiceName', e.target.value)}
            placeholder="e.g. Advanced Dermatology Associates" />
          {errors.practiceName && <p className="text-[10px] text-red-500 mt-0.5">{errors.practiceName}</p>}
        </div>
      </div>

      {/* ── Locations ── */}
      <div className="border-t border-slate-100 pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
            Practice Locations ({locations.length})
          </span>
          <button type="button" onClick={addLocation}
            className="text-[10px] font-bold text-teal-600 hover:text-teal-700 flex items-center gap-1 transition-colors">
            <span className="text-sm leading-none">+</span> Add Location
          </button>
        </div>

        {locations.map((loc, idx) => (
          <div key={idx} className="border border-slate-200 rounded-xl p-3.5 space-y-2.5 relative bg-slate-50/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="w-5 h-5 rounded-md bg-teal-100 text-teal-600 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                  {idx + 1}
                </span>
                <input
                  className="text-xs font-semibold text-slate-700 bg-transparent border-0 p-0 focus:outline-none focus:ring-0 w-full placeholder:text-slate-300"
                  value={loc.label}
                  onChange={e => updateLocation(idx, 'label', e.target.value)}
                  placeholder="Location name (e.g. Main Office, Satellite - Plano)"
                />
              </div>
              {locations.length > 1 && (
                <button type="button" onClick={() => removeLocation(idx)}
                  className="text-slate-300 hover:text-red-500 transition-colors ml-2 flex-shrink-0 p-1">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
            <div>
              <input className={InputClass} value={loc.address}
                onChange={e => updateLocation(idx, 'address', e.target.value)}
                placeholder="Full address (e.g. 123 Medical Center Dr, Suite 200, Dallas, TX 75201)" />
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <input className={InputClass} value={loc.phone}
                onChange={e => updateLocation(idx, 'phone', e.target.value)}
                placeholder="Phone: (555) 123-4567" />
              <input className={InputClass} value={loc.fax}
                onChange={e => updateLocation(idx, 'fax', e.target.value)}
                placeholder="Fax: (555) 123-4568" />
            </div>
          </div>
        ))}

        {locations.length === 1 && (
          <p className="text-[10px] text-slate-400 flex items-center gap-1.5">
            <svg className="w-3 h-3 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Doctor practices at multiple locations? Click "+ Add Location" to add more.
          </p>
        )}
      </div>

      <div className="flex gap-3 pt-2 sticky bottom-0 bg-white pb-1 z-10">
        <button type="button" onClick={onCancel}
          className="flex-1 py-2.5 rounded-xl font-semibold text-sm border border-slate-200 text-slate-500 hover:bg-slate-50 transition-all">
          Cancel
        </button>
        <button type="submit"
          className="flex-1 py-2.5 rounded-xl font-semibold text-sm bg-teal-500 hover:bg-teal-600 text-white shadow-lg shadow-teal-500/20 transition-all">
          {doctor ? 'Save Changes' : 'Add Doctor'}
        </button>
      </div>
    </form>
  )
}

// ── Specialty Options ────────────────────────────────────────────────────
const SPECIALTIES = [
  'Allergy & Immunology','Cardiology','Dermatology','Endocrinology',
  'Gastroenterology','Neurology','Oncology','Ophthalmology',
  'Pain Management','Primary Care','Psychiatry','Pulmonology',
  'Rheumatology','Urology','Other',
]

// ── Main App ──────────────────────────────────────────────────────────────
const DEMO_USER: UserData = {
  email: 'demo@rxcheckup.ai', name: 'Demo User', plan: 'professional', loggedIn: true, lmnCount: 12,
  trialLmnLimit: 5, trialActive: false, billingStarted: true, isAdmin: false,
  referralCode: 'DEMO-2026', referredBy: null, referralCount: 0, referralPaidCount: 0,
  freeMonthsEarned: 0, freeMonthsUsed: 0,
  monthlyLmnCount: 12, monthlyLmnLimit: 200, bonusLmnCount: 0, bonusLmnUsed: 0,
  currentMonth: new Date().toISOString().slice(0, 7),
  createdAt: new Date().toISOString(),
  paymentFailed: false, cancelAtPeriodEnd: false, subscriptionEndsAt: null, currentPeriodEnd: null,
}

export default function App({ demoMode = false }: { demoMode?: boolean } = {}) {
  const navigate = useNavigate()
  const toast = useToast()

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('theme') === 'dark')

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    }
  }, [darkMode])

  const [carrier, setCarrier]           = useState('')
  const [planType, setPlanType]         = useState('')
  const [selectedPlan, setSelectedPlan] = useState<PlanResult | null>(null)
  const [selectedIcd10, setSelectedIcd10] = useState<ICD10Result[]>([])
  const [deniedProduct, setDeniedProduct] = useState('')
  const [denialReason, setDenialReason] = useState('')
  const [selectedPriorTx, setSelectedPriorTx] = useState<string[]>([])
  const [specialist, setSpecialist]     = useState(false)
  const [prevApproved, setPrevApproved] = useState(false)
  const [prevPayer, setPrevPayer]       = useState('')

  // Bulk generation state
  const [formMode, setFormMode] = useState<'single' | 'bulk'>('single')
  const emptyBulkRow = (): BulkLMNItemRequest => ({ carrier: '', plan_type: '', icd10_codes: [], denied_product: '', denial_reason: '', reference_tag: '' })
  const [bulkRows, setBulkRows] = useState<BulkLMNItemRequest[]>([emptyBulkRow()])
  const [bulkLoading, setBulkLoading] = useState(false)
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number } | null>(null)
  const [bulkResults, setBulkResults] = useState<{ total: number; successful: number; failed: number; results: BulkLMNResultItem[] } | null>(null)
  const [bulkError, setBulkError] = useState<string | null>(null)

  // Continuation of care state
  const [continuityLaw, setContinuityLaw] = useState<{
    found: boolean; state_code?: string; state_name?: string; statute?: string;
    title?: string; summary?: string; transition_period?: string; message?: string;
  } | null>(null)
  const [continuityLoading, setContinuityLoading] = useState(false)

  // User account state
  const [userData, setUserData] = useState<UserData>(demoMode ? DEMO_USER : getUserData)
  const trialRemaining = Math.max(0, userData.trialLmnLimit - userData.lmnCount)
  const isInTrial = userData.trialActive && !userData.billingStarted
  const trialExpired = isInTrial && trialRemaining <= 0

  // Sync user data from backend on mount
  useEffect(() => {
    if (demoMode) return // Skip API sync in demo mode
    apiGetMe().then(user => {
      const synced: UserData = {
        email: user.email,
        name: user.name,
        plan: user.plan,
        loggedIn: true,
        lmnCount: user.lmn_count,
        trialLmnLimit: user.trial_lmn_limit,
        trialActive: user.trial_active,
        billingStarted: user.billing_started,
        isAdmin: user.is_admin || false,
        referralCode: user.referral_code,
        referredBy: user.referred_by,
        referralCount: user.referral_count,
        referralPaidCount: user.referral_paid_count,
        freeMonthsEarned: user.free_months_earned,
        freeMonthsUsed: user.free_months_used,
        monthlyLmnCount: user.monthly_lmn_count,
        monthlyLmnLimit: user.monthly_lmn_limit,
        bonusLmnCount: 0, bonusLmnUsed: 0,
        currentMonth: user.current_month,
        createdAt: user.created_at,
        paymentFailed: false, cancelAtPeriodEnd: false, subscriptionEndsAt: null, currentPeriodEnd: null,
      }
      // Store is_admin in localStorage for route guard
      localStorage.setItem(USER_KEY, JSON.stringify({ is_admin: synced.isAdmin }))
      setUserData(synced)
      updateUserData(synced)
      // Also fetch billing status for payment/cancellation state
      apiBillingStatus().then(billing => {
        setUserData(prev => ({
          ...prev,
          paymentFailed: billing.payment_failed || false,
          cancelAtPeriodEnd: billing.cancel_at_period_end || false,
          subscriptionEndsAt: billing.subscription_ends_at || null,
          currentPeriodEnd: billing.current_period_end || null,
          monthlyLmnCount: billing.monthly_lmn_count,
          monthlyLmnLimit: billing.monthly_lmn_limit,
          bonusLmnCount: billing.bonus_lmn_count || 0,
          bonusLmnUsed: billing.bonus_lmn_used || 0,
        }))
      }).catch(() => {})
    }).catch(() => {
      // If token is invalid, redirect to login
      clearTokens()
      navigate('/login')
    })
  }, [])

  const [showReferralPanel, setShowReferralPanel] = useState(false)
  const [referralCopied, triggerReferralCopied] = useTemporaryState(false, 2000)
  const [usageAlert, setUsageAlert] = useState<UsageAlert | null>(null)

  // Doctor profile state — synced with backend API
  const [doctors, setDoctors] = useState<DoctorProfile[]>([])
  const [doctorsLoading, setDoctorsLoading] = useState(true)
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | null>(null)
  const [showDoctorModal, setShowDoctorModal] = useState(false)
  const [editingDoctor, setEditingDoctor] = useState<DoctorProfile | null>(null)
  const [showDoctorManager, setShowDoctorManager] = useState(false)
  const [selectedLocationIdx, setSelectedLocationIdx] = useState(0)
  const [doctorSaving, setDoctorSaving] = useState(false)
  const selectedDoctor = doctors.find(d => d.id === selectedDoctorId) || null
  const activeLocation = selectedDoctor ? getActiveLocation(selectedDoctor, selectedLocationIdx) : null
  const [doctorLimit, setDoctorLimit] = useState(getDoctorLimit())

  // Fetch continuation of care law when "previously approved" is checked and address is available
  useEffect(() => {
    if (!prevApproved) { setContinuityLaw(null); return }
    const address = activeLocation?.address || ''
    if (!address) { setContinuityLaw(null); return }
    // Extract state from address client-side (simple regex)
    const stateMatch = address.match(/,\s*([A-Z]{2})\s+\d{5}/) || address.match(/,\s*([A-Z]{2})\s*$/)
    const stateCode = stateMatch?.[1]
    if (!stateCode) { setContinuityLaw(null); return }
    setContinuityLoading(true)
    api.get(`/api/continuity-of-care`, { params: { state: stateCode, plan_type: planType || undefined } })
      .then(res => setContinuityLaw(res.data))
      .catch(() => setContinuityLaw(null))
      .finally(() => setContinuityLoading(false))
  }, [prevApproved, activeLocation?.address, planType])

  // Billing / upgrade state
  const [showBillingModal, setShowBillingModal] = useState(false)
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly')
  const [showTopupModal, setShowTopupModal] = useState(false)
  const [topupLoading, setTopupLoading] = useState<string | null>(null)
  const [billingLoading, setBillingLoading] = useState(false)
  const [billingError, setBillingError] = useState<string | null>(null)
  const [billingSuccess, setBillingSuccess] = useState<string | null>(null)
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [invoicesLoading, setInvoicesLoading] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)

  // LMN history removed — Appeals dashboard handles all history/search

  // Support ticket state
  const [showSupportModal, setShowSupportModal] = useState(false)
  const [supportSubject, setSupportSubject] = useState('')
  const [supportMessage, setSupportMessage] = useState('')
  const [supportCategory, setSupportCategory] = useState('general')
  const [supportSubmitting, setSupportSubmitting] = useState(false)
  const [supportSuccess, setSupportSuccess] = useState(false)
  const [supportError, setSupportError] = useState<string | null>(null)

  // Account settings state
  const [showAccountSettings, setShowAccountSettings] = useState(false)
  const [accountTab, setAccountTab] = useState<'profile' | 'password' | 'subscription' | 'team' | 'branding'>('profile')
  const [profileName, setProfileName] = useState('')
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileSuccess, triggerProfileSuccess, resetProfileSuccess] = useTemporaryState(false, 3000)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordSaving, setPasswordSaving] = useState(false)
  const [passwordSuccess, triggerPasswordSuccess, resetPasswordSuccess] = useTemporaryState(false, 3000)
  const [passwordError, setPasswordError] = useState<string | null>(null)

  // Branding / white-label state
  const [brandingData, setBrandingData] = useState<BrandingData | null>(null)
  const [brandingLoading, setBrandingLoading] = useState(false)
  const [brandingSaving, setBrandingSaving] = useState(false)
  const [brandingSuccess, triggerBrandingSuccess, resetBrandingSuccess] = useTemporaryState(false, 3000)
  const [brandingError, setBrandingError] = useState<string | null>(null)
  const [brandingPrimaryColor, setBrandingPrimaryColor] = useState('#0d9488')
  const [brandingAccentColor, setBrandingAccentColor] = useState('#3b82f6')
  const [brandingDisplayName, setBrandingDisplayName] = useState('')
  const [brandingFooterText, setBrandingFooterText] = useState('')
  const [brandingEmailHeader, setBrandingEmailHeader] = useState('')
  const [brandingHidePoweredBy, setBrandingHidePoweredBy] = useState(false)
  const [brandingLetterheadEnabled, setBrandingLetterheadEnabled] = useState(false)
  const [brandingLetterheadHtml, setBrandingLetterheadHtml] = useState('')
  const [brandingLogoUploading, setBrandingLogoUploading] = useState(false)

  // Team management
  const [teamData, setTeamData] = useState<TeamData | null>(null)
  const [teamLoading, setTeamLoading] = useState(false)
  const [showAddMember, setShowAddMember] = useState(false)
  const [newMemberName, setNewMemberName] = useState('')
  const [newMemberEmail, setNewMemberEmail] = useState('')
  const [newMemberPassword, setNewMemberPassword] = useState('')
  const [teamSaving, setTeamSaving] = useState(false)
  const [teamError, setTeamError] = useState<string | null>(null)
  const [teamSuccess, triggerTeamSuccess, resetTeamSuccess] = useTemporaryState<string | null>(null, 3000)

  // Appeals dashboard
  const [showAppealsDashboard, setShowAppealsDashboard] = useState(false)

  // What's Next panel
  const [whatsNextCollapsed, setWhatsNextCollapsed] = useState(false)

  // Payer directory
  const [showPayerDirectory, setShowPayerDirectory] = useState(false)

  // Keyboard shortcuts help modal
  const [showShortcutsModal, setShowShortcutsModal] = useState(false)

  // Onboarding walkthrough (existing tour modal)
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return !localStorage.getItem('checkup_onboarding_done')
  })
  const [onboardingStep, setOnboardingStep] = useState(0)

  // Onboarding checklist (persistent card for new users)
  const [onboardingDismissed, setOnboardingDismissed] = useState(() => {
    return localStorage.getItem('onboarding_dismissed') === 'true'
  })

  // First-run welcome overlay (shows only for brand-new users with 0 LMNs)
  const [showFirstRunWelcome, setShowFirstRunWelcome] = useState(() => {
    return localStorage.getItem('checkup_onboarding_dismissed') !== 'true'
  })
  const isFirstTimeUser = userData.lmnCount === 0 && userData.monthlyLmnCount === 0

  // Intelligence Hub panel (collapsed by default for existing users, expanded for new)
  const [intelligenceExpanded, setIntelligenceExpanded] = useState(() => {
    const stored = localStorage.getItem('checkup_intelligence_expanded')
    if (stored !== null) return stored === 'true'
    // Default: expanded for new users, collapsed for existing
    const u = getUserData()
    return u.lmnCount === 0 && u.monthlyLmnCount === 0
  })

  const toggleIntelligence = () => {
    setIntelligenceExpanded(prev => {
      localStorage.setItem('checkup_intelligence_expanded', String(!prev))
      return !prev
    })
  }

  // Intelligence feature access based on plan
  const hasApprovalPredictor = ['professional', 'professional_plus', 'enterprise'].includes(userData.plan)
  const hasSmartAlerts = ['professional_plus', 'enterprise'].includes(userData.plan)
  const hasCommunityInsights = ['professional_plus', 'enterprise'].includes(userData.plan)

  // Practice Performance summary
  const [perfData, setPerfData] = useState<AppealsDashboardData | null>(null)
  const [perfLoading, setPerfLoading] = useState(false)

  // Page title
  useEffect(() => { document.title = 'Dashboard — RxCheckUp' }, [])

  // Fetch practice performance data for summary bar
  useEffect(() => {
    let cancelled = false
    async function loadPerfData() {
      setPerfLoading(true)
      try {
        const data = await apiAppealsDashboard()
        if (!cancelled) setPerfData(data)
      } catch {
        // Silently fail — summary bar will show placeholder state
      } finally {
        if (!cancelled) setPerfLoading(false)
      }
    }
    loadPerfData()
    return () => { cancelled = true }
  }, [])

  // Global keyboard handler — moved after handleGenerate/handleDownload definitions

  // Load doctors from backend API on mount
  const loadDoctorsFromAPI = useCallback(async () => {
    if (demoMode) {
      const demoDoctors: DoctorProfile[] = [
        { id: 'demo-1', name: 'Sarah Mitchell, MD, FACR', npi: '1234567890', specialty: 'Rheumatology', dea: '', practiceName: 'Central Valley Medical Group', locations: [{ label: 'Main Office', address: '4500 Medical Center Dr, Suite 210 Fresno, CA 93721', phone: '(559) 555-0100', fax: '(559) 555-0101' }] },
        { id: 'demo-2', name: 'James Park, MD', npi: '0987654321', specialty: 'Dermatology', dea: '', practiceName: 'Central Valley Medical Group', locations: [{ label: 'Main Office', address: '4500 Medical Center Dr, Suite 210 Fresno, CA 93721', phone: '(559) 555-0100', fax: '(559) 555-0101' }] },
      ]
      setDoctors(demoDoctors)
      setDoctorLimit(8)
      setSelectedDoctorId('demo-1')
      setDoctorsLoading(false)
      return
    }
    try {
      setDoctorsLoading(true)
      const data = await apiListDoctors()
      const mapped: DoctorProfile[] = data.doctors.map(d => ({
        id: d.id,
        name: d.name,
        npi: d.npi,
        specialty: d.specialty,
        dea: d.dea,
        practiceName: d.practiceName,
        locations: d.locations || [{ label: 'Main Office', address: '', phone: '', fax: '' }],
      }))
      setDoctors(mapped)
      setDoctorLimit(data.limit)
      if (mapped.length > 0 && !selectedDoctorId) {
        setSelectedDoctorId(mapped[0].id)
      }
    } catch {
      // If API fails, doctors list stays empty
    } finally {
      setDoctorsLoading(false)
    }
  }, [demoMode])

  useEffect(() => { loadDoctorsFromAPI() }, [loadDoctorsFromAPI])

  // Pre-fill demo with static illustrative data
  useEffect(() => {
    if (!demoMode) return
    // Pre-fill form fields
    setCarrier('Aetna')
    setPlanType('Commercial PPO')
    setSelectedIcd10([{ code: 'L40.0', description: 'Psoriasis vulgaris', body_system: 'Skin' }, { code: 'L40.50', description: 'Arthropathic psoriasis, unspecified', body_system: 'Skin' }])
    setDeniedProduct('Skyrizi (risankizumab)')
    setDenialReason('Step Therapy / Fail First')
    setSelectedPriorTx(['Humira (adalimumab)', 'Otezla (apremilast)'])
    setSpecialist(true)

    // Pre-fill a static illustrative letter result
    const demoLetterText = `Date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}

Re: Letter of Medical Necessity — Prior Authorization Appeal
Patient: [Patient Name]
DOB: [Date of Birth]
Member ID: [Member ID]
Carrier: Aetna — Commercial PPO
Medication: Skyrizi (risankizumab-rzaa) 150mg subcutaneous injection

Dear Medical Director,

I am writing on behalf of my patient to request authorization for Skyrizi (risankizumab-rzaa) for the treatment of moderate-to-severe plaque psoriasis (ICD-10: L40.0) with concurrent psoriatic arthritis (ICD-10: L40.50). This medication is medically necessary as outlined below.

CLINICAL HISTORY & DIAGNOSIS:
The patient presents with moderate-to-severe plaque psoriasis affecting approximately 15% body surface area (BSA), with plaques on the trunk, extremities, and scalp. The patient also has concurrent psoriatic arthritis with active joint involvement in the hands and feet, causing significant functional impairment. PASI score is 14.2 and DLQI score is 18, indicating severe impact on quality of life.

PRIOR TREATMENT FAILURES:
1. Humira (adalimumab) — Initiated 03/2024, discontinued 09/2024. Patient achieved initial PASI-50 response but experienced secondary loss of efficacy by month 4, with anti-drug antibody development confirmed. BSA remained >10% at discontinuation.

2. Otezla (apremilast) — Initiated 10/2024, discontinued 02/2025. Patient experienced intolerable GI side effects (nausea, diarrhea, weight loss of 12 lbs) and achieved only PASI-25 response after 16 weeks, failing to meet therapeutic goals.

MEDICAL JUSTIFICATION FOR SKYRIZI:
Skyrizi (risankizumab) is an IL-23 inhibitor with a distinct mechanism of action from the previously failed therapies. Per the AAD-NPF Joint Guidelines for Psoriasis (2024), IL-23 inhibitors are recommended as preferred biologic therapy after TNF-alpha inhibitor failure. Key clinical evidence supporting this request:

• UltIMMa-1 & UltIMMa-2 trials demonstrated 75% of patients achieved PASI-90 at week 16, with sustained efficacy through 52 weeks (Gordon et al., Lancet 2018)
• KEEPsAKE-1 trial showed significant improvement in psoriatic arthritis symptoms with ACR20 response rate of 57% vs 33.5% placebo (Kristensen et al., Ann Rheum Dis 2022)
• Favorable safety profile with no requirement for routine laboratory monitoring

CLINICAL NECESSITY:
Without adequate treatment, this patient faces progressive joint destruction, permanent disability, increased cardiovascular risk associated with chronic systemic inflammation, and continued severe impact on daily functioning and mental health. The two prior biologic/targeted synthetic DMARD failures satisfy the step therapy requirement per Aetna Clinical Policy Bulletin 0814.

I respectfully request that Skyrizi be approved for this patient. I am available for a peer-to-peer review at your convenience.

Sincerely,

Sarah Mitchell, MD, FACR
Rheumatology
NPI: 1234567890
Central Valley Medical Group
4500 Medical Center Dr, Suite 210
Fresno, CA 93721
Phone: (559) 555-0100 | Fax: (559) 555-0101

[DEMO MODE — This is an illustrative sample letter. Sign up for a free trial to generate AI-powered, carrier-specific letters with real clinical citations.]`

    const demoResult = {
      success: true as const,
      letter_text: demoLetterText,
      letter_sections: {},
      citations: [
        { number: 1, source: 'AAD-NPF', title: 'Joint AAD-NPF Guidelines of Care for the Management of Psoriasis with Biologics, 2024 Update' },
        { number: 2, source: 'Lancet', title: 'Gordon KB et al. Efficacy and safety of risankizumab in moderate-to-severe plaque psoriasis (UltIMMa-1 and UltIMMa-2). Lancet 2018;392:650-661' },
        { number: 3, source: 'Ann Rheum Dis', title: 'Kristensen LE et al. Efficacy and safety of risankizumab for psoriatic arthritis (KEEPsAKE-1). Ann Rheum Dis 2022;81:1468-1476' },
        { number: 4, source: 'Aetna', title: 'Aetna Clinical Policy Bulletin 0814: Plaque Psoriasis — Biologic and Targeted Synthetic Agents' },
      ] as Citation[],
      provider_fill_ins: [] as FillIn[],
      diagnosis_descriptions: ['L40.0 — Psoriasis vulgaris', 'L40.50 — Arthropathic psoriasis, unspecified'],
      denial_rebuttal_strategy: 'Step therapy fulfilled — two prior treatment failures documented with clinical evidence supporting IL-23 inhibitor as next-line therapy per AAD-NPF guidelines',
      generation_mode: 'demo',
    }
    setResult(demoResult)
    setEditedLetter(demoResult.letter_text)
    setActiveTab('letter')
    setWhatsNextCollapsed(false)
  }, [demoMode])

  const handleSaveDoctor = async (doc: DoctorProfile) => {
    setDoctorSaving(true)
    try {
      const isEdit = doctors.some(d => d.id === doc.id)
      const payload = {
        name: doc.name,
        npi: doc.npi,
        specialty: doc.specialty,
        dea: doc.dea || undefined,
        practice_name: doc.practiceName,
        locations: doc.locations.filter(l => l.label || l.address || l.phone || l.fax),
      }

      if (isEdit) {
        const updated = await apiUpdateDoctor(doc.id, payload)
        setDoctors(prev => prev.map(d => d.id === doc.id ? {
          id: updated.id, name: updated.name, npi: updated.npi, specialty: updated.specialty,
          dea: updated.dea, practiceName: updated.practiceName, locations: updated.locations,
        } : d))
      } else {
        const created = await apiCreateDoctor(payload)
        setDoctors(prev => [...prev, {
          id: created.id, name: created.name, npi: created.npi, specialty: created.specialty,
          dea: created.dea, practiceName: created.practiceName, locations: created.locations,
        }])
        setSelectedDoctorId(created.id)
      }
      setSelectedLocationIdx(0)
      setShowDoctorModal(false)
      setEditingDoctor(null)
    } catch (e: unknown) {
      const msg = getApiErrorMessage(e, 'Failed to save doctor')
      toast.error(String(msg))
    } finally {
      setDoctorSaving(false)
    }
  }

  const handleDeleteDoctor = async (id: string) => {
    try {
      await apiDeleteDoctor(id)
      setDoctors(prev => {
        const updated = prev.filter(d => d.id !== id)
        if (selectedDoctorId === id) {
          setSelectedDoctorId(updated.length > 0 ? updated[0].id : null)
        }
        return updated
      })
    } catch (e: unknown) {
      const msg = getApiErrorMessage(e, 'Failed to delete doctor')
      toast.error(String(msg))
    }
  }

  // Billing handlers
  const handleUpgrade = async (plan: string) => {
    setBillingLoading(true)
    setBillingError(null)
    setBillingSuccess(null)
    try {
      if (userData.billingStarted && userData.plan !== plan) {
        // Mid-cycle plan change
        const data = await apiChangePlan(plan)
        setBillingSuccess(data.message)
        // Refresh user data
        const user = await apiGetMe()
        setUserData(prev => ({
          ...prev,
          plan: user.plan,
          monthlyLmnLimit: user.monthly_lmn_limit,
          billingStarted: user.billing_started,
          cancelAtPeriodEnd: false,
          subscriptionEndsAt: null,
        }))
        updateUserData({ plan: user.plan, monthlyLmnLimit: user.monthly_lmn_limit })
      } else {
        // New subscription checkout
        const data = await apiCreateCheckout(plan, billingPeriod)
        if (data.checkout_url) {
          window.location.href = data.checkout_url
        }
      }
    } catch (e: unknown) {
      const msg = getApiErrorMessage(e, 'Failed to start checkout')
      setBillingError(String(msg))
    } finally {
      setBillingLoading(false)
    }
  }

  const handleManageBilling = async () => {
    try {
      const data = await apiBillingPortal()
      if (data.portal_url) {
        window.open(data.portal_url, '_blank')
      }
    } catch (e: unknown) {
      const msg = getApiErrorMessage(e, 'No active subscription found')
      toast.error(String(msg))
    }
  }

  const handleCancelSubscription = async () => {
    setCancelLoading(true)
    setBillingError(null)
    try {
      const data = await apiCancelSubscription()
      setBillingSuccess(data.message)
      setUserData(prev => ({ ...prev, cancelAtPeriodEnd: true, subscriptionEndsAt: data.ends_at }))
      updateUserData({ cancelAtPeriodEnd: true, subscriptionEndsAt: data.ends_at })
      setShowCancelConfirm(false)
    } catch (e: unknown) {
      const msg = getApiErrorMessage(e, 'Cancellation failed')
      setBillingError(String(msg))
    } finally {
      setCancelLoading(false)
    }
  }

  const handleReactivate = async () => {
    setBillingLoading(true)
    setBillingError(null)
    try {
      const data = await apiReactivateSubscription()
      setBillingSuccess(data.message)
      setUserData(prev => ({ ...prev, cancelAtPeriodEnd: false, subscriptionEndsAt: null }))
      updateUserData({ cancelAtPeriodEnd: false, subscriptionEndsAt: null })
    } catch (e: unknown) {
      const msg = getApiErrorMessage(e, 'Reactivation failed')
      setBillingError(String(msg))
    } finally {
      setBillingLoading(false)
    }
  }

  const handleRetryPayment = async () => {
    setBillingLoading(true)
    setBillingError(null)
    try {
      const data = await apiRetryPayment()
      if (data.success) {
        setBillingSuccess(data.message)
        setUserData(prev => ({ ...prev, paymentFailed: false }))
        updateUserData({ paymentFailed: false })
      } else {
        setBillingError(data.message)
      }
    } catch (e: unknown) {
      const msg = getApiErrorMessage(e, 'Payment retry failed')
      setBillingError(String(msg))
    } finally {
      setBillingLoading(false)
    }
  }

  const loadInvoices = async () => {
    setInvoicesLoading(true)
    try {
      const data = await apiListInvoices(12)
      setInvoices(data.invoices)
    } catch {
      setInvoices([])
    } finally {
      setInvoicesLoading(false)
    }
  }

  // LMN History handlers removed — Appeals dashboard handles all history/search

  const handleSubmitSupportTicket = async () => {
    if (!supportSubject.trim() || !supportMessage.trim()) return
    setSupportSubmitting(true)
    setSupportError(null)
    try {
      await apiSubmitSupportTicket({
        subject: supportSubject.trim(),
        message: supportMessage.trim(),
        source: 'in_app',
        category: supportCategory,
      })
      setSupportSuccess(true)
      setSupportSubject('')
      setSupportMessage('')
      setSupportCategory('general')
    } catch (err: any) {
      setSupportError(err?.response?.data?.detail || 'Failed to submit ticket. Please try again.')
    } finally {
      setSupportSubmitting(false)
    }
  }

  const handleOpenAccountSettings = () => {
    setProfileName(userData.name)
    resetProfileSuccess()
    setProfileError(null)
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    resetPasswordSuccess()
    setPasswordError(null)
    resetBrandingSuccess()
    setBrandingError(null)
    setAccountTab('profile')
    setShowAccountSettings(true)
  }

  const loadBrandingData = async () => {
    setBrandingLoading(true)
    setBrandingError(null)
    try {
      const data = await apiGetBranding()
      setBrandingData(data)
      setBrandingPrimaryColor(data.primary_color || '#0d9488')
      setBrandingAccentColor(data.accent_color || '#3b82f6')
      setBrandingDisplayName(data.practice_display_name || '')
      setBrandingFooterText(data.custom_footer_text || '')
      setBrandingEmailHeader(data.custom_email_header || '')
      setBrandingHidePoweredBy(data.hide_rxcheckup_branding || false)
      setBrandingLetterheadEnabled(data.letterhead_enabled || false)
      setBrandingLetterheadHtml(data.letterhead_html || '')
    } catch (err: any) {
      setBrandingError(err.response?.data?.detail || 'Failed to load branding settings.')
    } finally {
      setBrandingLoading(false)
    }
  }

  const handleSaveBranding = async () => {
    setBrandingSaving(true)
    resetBrandingSuccess()
    setBrandingError(null)
    try {
      const updated = await apiUpdateBranding({
        primary_color: brandingPrimaryColor,
        accent_color: brandingAccentColor,
        practice_display_name: brandingDisplayName || null,
        custom_footer_text: brandingFooterText || null,
        custom_email_header: brandingEmailHeader || null,
        hide_rxcheckup_branding: brandingHidePoweredBy,
        letterhead_enabled: brandingLetterheadEnabled,
        letterhead_html: brandingLetterheadHtml || null,
      })
      setBrandingData(updated)
      triggerBrandingSuccess()
    } catch (err: any) {
      setBrandingError(err.response?.data?.detail || 'Failed to save branding settings.')
    } finally {
      setBrandingSaving(false)
    }
  }

  const handleBrandingLogoUpload = async (file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      setBrandingError('Logo must be under 2MB.')
      return
    }
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (!ext || !['png', 'jpg', 'jpeg', 'svg'].includes(ext)) {
      setBrandingError('Logo must be PNG, JPG, or SVG.')
      return
    }
    setBrandingLogoUploading(true)
    setBrandingError(null)
    try {
      const result = await apiUploadLogo(file)
      setBrandingData(prev => prev ? { ...prev, practice_logo_url: result.logo_url } : prev)
    } catch (err: any) {
      setBrandingError(err.response?.data?.detail || 'Failed to upload logo.')
    } finally {
      setBrandingLogoUploading(false)
    }
  }

  const handleBrandingLogoDelete = async () => {
    setBrandingLogoUploading(true)
    setBrandingError(null)
    try {
      await apiDeleteLogo()
      setBrandingData(prev => prev ? { ...prev, practice_logo_url: null } : prev)
    } catch (err: any) {
      setBrandingError(err.response?.data?.detail || 'Failed to remove logo.')
    } finally {
      setBrandingLogoUploading(false)
    }
  }

  const handleResetBrandingColors = () => {
    setBrandingPrimaryColor('#0d9488')
    setBrandingAccentColor('#3b82f6')
  }

  const handleSaveProfile = async () => {
    if (!profileName.trim()) return
    setProfileSaving(true)
    setProfileError(null)
    resetProfileSuccess()
    try {
      const user = await apiUpdateProfile({ name: profileName.trim() })
      setUserData(prev => ({ ...prev, name: user.name }))
      triggerProfileSuccess()
    } catch (err: any) {
      setProfileError(err?.response?.data?.detail || 'Failed to update profile.')
    } finally {
      setProfileSaving(false)
    }
  }

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) return
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.')
      return
    }
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters.')
      return
    }
    setPasswordSaving(true)
    setPasswordError(null)
    resetPasswordSuccess()
    try {
      await apiChangePassword(currentPassword, newPassword)
      triggerPasswordSuccess()
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: any) {
      setPasswordError(err?.response?.data?.detail || 'Failed to change password.')
    } finally {
      setPasswordSaving(false)
    }
  }

  const loadTeamData = async () => {
    setTeamLoading(true)
    setTeamError(null)
    try {
      const data = await apiGetTeam()
      setTeamData(data)
    } catch (err: any) {
      setTeamError(err?.response?.data?.detail || 'Failed to load team data.')
    } finally {
      setTeamLoading(false)
    }
  }

  const handleAddTeamMember = async () => {
    if (!newMemberName.trim() || !newMemberEmail.trim() || !newMemberPassword) return
    setTeamSaving(true)
    setTeamError(null)
    resetTeamSuccess()
    try {
      const result = await apiAddTeamMember({
        name: newMemberName.trim(),
        email: newMemberEmail.trim(),
        password: newMemberPassword,
      })
      triggerTeamSuccess(result.message)
      setNewMemberName('')
      setNewMemberEmail('')
      setNewMemberPassword('')
      setShowAddMember(false)
      loadTeamData()
    } catch (err: any) {
      setTeamError(err?.response?.data?.detail || 'Failed to add team member.')
    } finally {
      setTeamSaving(false)
    }
  }

  const handleRemoveTeamMember = async (memberId: string, memberName: string) => {
    if (!confirm(`Remove ${memberName} from your team? They will lose access immediately.`)) return
    setTeamError(null)
    try {
      await apiRemoveTeamMember(memberId)
      triggerTeamSuccess(`${memberName} removed.`)
      loadTeamData()
    } catch (err: any) {
      setTeamError(err?.response?.data?.detail || 'Failed to remove team member.')
    }
  }

  // Check for billing=success in URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('billing') === 'success') {
      // Refresh user data after successful checkout
      apiGetMe().then(user => {
        const synced: UserData = {
          email: user.email, name: user.name, plan: user.plan, loggedIn: true,
          lmnCount: user.lmn_count, trialLmnLimit: user.trial_lmn_limit,
          trialActive: user.trial_active, billingStarted: user.billing_started,
          isAdmin: user.is_admin || false,
          referralCode: user.referral_code, referredBy: user.referred_by,
          referralCount: user.referral_count, referralPaidCount: user.referral_paid_count,
          freeMonthsEarned: user.free_months_earned, freeMonthsUsed: user.free_months_used,
          monthlyLmnCount: user.monthly_lmn_count, monthlyLmnLimit: user.monthly_lmn_limit,
          bonusLmnCount: 0, bonusLmnUsed: 0,
          currentMonth: user.current_month,
          createdAt: user.created_at,
          paymentFailed: false, cancelAtPeriodEnd: false, subscriptionEndsAt: null, currentPeriodEnd: null,
        }
        setUserData(synced)
        updateUserData(synced)
        loadDoctorsFromAPI() // Refresh doctor limits too
      }).catch(() => {})
      // Clean URL
      window.history.replaceState({}, '', '/app')
    }
    // Check for topup=success in URL params
    if (params.get('topup') === 'success') {
      // Refresh user data after successful top-up purchase
      apiGetMe().then(user => {
        const synced: UserData = {
          email: user.email, name: user.name, plan: user.plan, loggedIn: true,
          lmnCount: user.lmn_count, trialLmnLimit: user.trial_lmn_limit,
          trialActive: user.trial_active, billingStarted: user.billing_started,
          isAdmin: user.is_admin || false,
          referralCode: user.referral_code, referredBy: user.referred_by,
          referralCount: user.referral_count, referralPaidCount: user.referral_paid_count,
          freeMonthsEarned: user.free_months_earned, freeMonthsUsed: user.free_months_used,
          monthlyLmnCount: user.monthly_lmn_count, monthlyLmnLimit: user.monthly_lmn_limit,
          bonusLmnCount: 0, bonusLmnUsed: 0,
          currentMonth: user.current_month,
          createdAt: user.created_at,
          paymentFailed: false, cancelAtPeriodEnd: false, subscriptionEndsAt: null, currentPeriodEnd: null,
        }
        setUserData(synced)
        updateUserData(synced)
        // Fetch billing status to get updated bonus LMN counts
        apiBillingStatus().then(billing => {
          setUserData(prev => ({
            ...prev,
            bonusLmnCount: billing.bonus_lmn_count || 0,
            bonusLmnUsed: billing.bonus_lmn_used || 0,
          }))
        }).catch(() => {})
      }).catch(() => {})
      // Clean URL
      window.history.replaceState({}, '', '/app')
    }
  }, [])

  const handleLogout = async () => {
    await apiLogout()
    navigate('/login')
  }

  const [loading, setLoading]   = useState(false)
  const [result, setResult]     = useState<LMNResult | null>(null)
  const [error, setError]       = useState<string | null>(null)
  const [editedLetter, setEditedLetter] = useState('')
  const [activeTab, setActiveTab] = useState<'letter' | 'citations' | 'checklist'>('letter')
  const [copied, triggerCopied] = useTemporaryState(false, 2000)
  const [medicalPolicy, setMedicalPolicy] = useState<MedicalPolicy | null>(null)
  const [policyLoading, setPolicyLoading] = useState(false)

  // Saved Templates (Favorites) state
  const [savedTemplates, setSavedTemplates] = useState<SavedTemplateData[]>([])
  const [showSavedTemplates, setShowSavedTemplates] = useState(false)
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false)
  const [saveTemplateName, setSaveTemplateName] = useState('')
  const [saveTemplateLoading, setSaveTemplateLoading] = useState(false)
  const [saveTemplateSuccess, triggerSaveTemplateSuccess] = useTemporaryState(false, 3000)
  const savedTemplatesRef = useRef<HTMLDivElement>(null)

  // Load saved templates on mount
  useEffect(() => {
    if (demoMode) return
    apiGetSavedTemplates().then(res => setSavedTemplates(res.templates)).catch(() => {})
  }, [demoMode])

  // Close saved templates dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (savedTemplatesRef.current && !savedTemplatesRef.current.contains(e.target as Node)) {
        setShowSavedTemplates(false)
      }
    }
    if (showSavedTemplates) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showSavedTemplates])

  const handleUseSavedTemplate = async (template: SavedTemplateData) => {
    setCarrier(template.carrier)
    setPlanType(template.plan_type)
    setSelectedPlan(template.plan_name ? {
      plan_name: template.plan_name, contract_id: '', plan_id: '', formulary_id: '',
      plan_category: template.plan_type === 'medicare_advantage' || template.plan_type === 'medicare_part_d' ? 'Medicare' : template.plan_type === 'medicaid_managed' || template.plan_type === 'managed_medicaid' ? 'Medicaid' : template.plan_type === 'exchange_marketplace' ? 'Marketplace' : template.plan_type === 'tricare' ? 'TRICARE' : template.plan_type === 'workers_comp' ? 'Workers Comp' : 'Commercial',
      plan_type_detail: '', segment_count: 0,
    } : null)
    setSelectedIcd10(template.icd10_codes.map(code => ({ code, description: '', body_system: '' })))
    setDeniedProduct(template.denied_product)
    setDenialReason(template.denial_reason)
    if (template.prior_treatments && template.prior_treatments.length > 0) {
      setSelectedPriorTx(template.prior_treatments)
    }
    setShowSavedTemplates(false)
    // Mark as used
    try { await apiUseSavedTemplate(template.id) } catch {}
    // Refresh list (reorder by last_used)
    apiGetSavedTemplates().then(res => setSavedTemplates(res.templates)).catch(() => {})
  }

  const handleDeleteSavedTemplate = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await apiDeleteSavedTemplate(id)
      setSavedTemplates(prev => prev.filter(t => t.id !== id))
    } catch {}
  }

  const handleSaveTemplate = async () => {
    if (!saveTemplateName.trim()) return
    setSaveTemplateLoading(true)
    try {
      const icd10Codes = selectedIcd10.map(s => s.code)
      const data: Record<string, unknown> = {
        name: saveTemplateName.trim(),
        carrier,
        plan_type: planType,
        icd10_codes: icd10Codes,
        denied_product: deniedProduct,
        denial_reason: denialReason,
      }
      if (selectedPlan?.plan_name) data.plan_name = selectedPlan.plan_name
      if (selectedPriorTx.length > 0) data.prior_treatments = selectedPriorTx
      const saved = await apiSaveTemplate(data as any)
      setSavedTemplates(prev => [saved, ...prev])
      setShowSaveTemplateModal(false)
      triggerSaveTemplateSuccess()
    } catch {}
    setSaveTemplateLoading(false)
  }

  // P2P Prep Sheet state
  const [p2pLoading, setP2pLoading] = useState(false)
  const [showP2pModal, setShowP2pModal] = useState(false)
  const [p2pData, setP2pData] = useState<P2PData | null>(null)
  const [p2pText, setP2pText] = useState('')
  const [p2pHtml, setP2pHtml] = useState('')
  const [p2pExpandedSections, setP2pExpandedSections] = useState<Record<string, boolean>>({
    opening: true, talking_points: true, objections: true, clinical: false, carrier_tips: false, closing: true, unsuccessful: false,
  })

  // Formulary upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadPolicyFile, setUploadPolicyFile] = useState<File | null>(null)
  const [uploadCarrier, setUploadCarrier] = useState('')
  const [uploadPlanName, setUploadPlanName] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState<{
    success: boolean; status: string; message?: string;
    validation?: { valid: boolean; matched_terms: string[]; reason: string };
    import_result?: { rows_imported: number; errors: string[] };
    formulary_uploaded?: boolean; policy_uploaded?: boolean;
  } | null>(null)

  const handleAddPlanAndUpload = async () => {
    if (!uploadCarrier || !uploadPlanName) return
    setUploading(true)
    setUploadResult(null)

    try {
      // Step 1: Register the custom plan
      const planFd = new FormData()
      planFd.append('carrier', uploadCarrier)
      planFd.append('plan_name', uploadPlanName)
      planFd.append('plan_type', planType || 'Commercial')
      await api.post('/api/plans/add-custom', planFd)

      let formularyOk = false
      let policyOk = false
      let validationError: string | null = null

      // Step 2: Upload formulary if provided
      if (uploadFile) {
        const fd = new FormData()
        fd.append('file', uploadFile)
        fd.append('carrier', uploadCarrier)
        fd.append('plan_name', uploadPlanName)
        fd.append('file_type', 'formulary')
        try {
          const res = await api.post('/api/formulary/upload', fd)
          formularyOk = res.data.success
        } catch (e: unknown) {
          if (axios.isAxiosError(e) && e.response?.status === 422) {
            validationError = e.response.data?.message || 'Formulary does not appear to be associated with this plan.'
            setUploadResult({
              success: false, status: 'validation_failed',
              message: validationError ?? undefined,
              validation: e.response.data?.validation,
            })
            setUploading(false)
            return
          }
          throw e
        }
      }

      // Step 3: Upload medical policy if provided
      if (uploadPolicyFile) {
        const fd = new FormData()
        fd.append('file', uploadPolicyFile)
        fd.append('carrier', uploadCarrier)
        fd.append('plan_name', uploadPlanName)
        fd.append('file_type', 'medical_policy')
        try {
          const res = await api.post('/api/formulary/upload', fd)
          policyOk = res.data.success
        } catch (e: unknown) {
          if (axios.isAxiosError(e) && e.response?.status === 422) {
            validationError = e.response.data?.message || 'Policy document does not appear to be associated with this plan.'
            setUploadResult({
              success: false, status: 'validation_failed',
              message: validationError ?? undefined,
              validation: e.response.data?.validation,
            })
            setUploading(false)
            return
          }
          throw e
        }
      }

      setUploadResult({
        success: true, status: 'plan_added',
        message: `"${uploadPlanName}" added for ${uploadCarrier}`,
        formulary_uploaded: formularyOk,
        policy_uploaded: policyOk,
      })

      // Auto-select the new plan — mark as unverified if no formulary was successfully uploaded
      const isVerified = formularyOk
      setSelectedPlan({
        plan_name: uploadPlanName,
        contract_id: '', plan_id: '', formulary_id: '',
        plan_category: planType === 'medicare_advantage' || planType === 'medicare_part_d' ? 'Medicare' : planType === 'medicaid_managed' || planType === 'managed_medicaid' ? 'Medicaid' : planType === 'exchange_marketplace' ? 'Marketplace' : planType === 'tricare' ? 'TRICARE' : planType === 'workers_comp' ? 'Workers Comp' : 'Commercial',
        plan_type_detail: 'Custom', segment_count: 0,
        unverified: !isVerified,
      })

    } catch (e: unknown) {
      const msg = getApiErrorMessage(e, 'Failed to add plan')
      setUploadResult({ success: false, status: 'error', message: String(msg) })
    } finally {
      setUploading(false)
    }
  }

  const icd10Codes = selectedIcd10.map(s => s.code)
  const canSubmit = carrier && planType && icd10Codes.length > 0 && deniedProduct && denialReason

  useEffect(() => {
    if (!carrier || !deniedProduct || icd10Codes.length === 0) {
      setMedicalPolicy(null)
      return
    }
    setPolicyLoading(true)
    axios.get<MedicalPolicy>('/api/medical-policy/lookup', {
      params: { carrier, drug: deniedProduct, icd10_codes: icd10Codes.join(',') }
    })
      .then(res => setMedicalPolicy(res.data))
      .catch(() => setMedicalPolicy(null))
      .finally(() => setPolicyLoading(false))
  }, [carrier, deniedProduct, icd10Codes.join(',')])

  // Monthly usage tracking
  const bonusRemaining = Math.max(0, (userData.bonusLmnCount || 0) - (userData.bonusLmnUsed || 0))
  const totalMonthlyAvailable = userData.monthlyLmnLimit + bonusRemaining
  const monthlyRemaining = Math.max(0, totalMonthlyAvailable - userData.monthlyLmnCount)
  const monthlyLimitReached = userData.billingStarted && monthlyRemaining <= 0

  const handleGenerate = async () => {
    // Demo mode: generate sample letter without API
    if (demoMode) {
      // Demo mode: letter is already pre-loaded, just scroll to it
      setActiveTab('letter')
      document.getElementById('letter-output')?.scrollIntoView({ behavior: 'smooth' })
      return
    }

    // Block if free trial is expired and billing hasn't started
    if (trialExpired) {
      setError('Your free trial of 5 LMNs has been used. Please add a payment method to continue generating letters.')
      return
    }
    // Block if monthly limit reached
    if (monthlyLimitReached) {
      setError(`You've used all ${userData.monthlyLmnLimit} LMNs for this month on your ${userData.plan === 'professional_plus' ? 'Professional+' : userData.plan} plan. Upgrade or wait until next month.`)
      return
    }

    setLoading(true); setError(null); setResult(null); setUsageAlert(null)
    try {
      const payload: Record<string, unknown> = {
        carrier, plan_type: planType,
        icd10_codes: icd10Codes,
        denied_product: deniedProduct,
        denial_reason: denialReason,
        specialist_involved: specialist || undefined,
        previous_pa_approved: prevApproved || undefined,
      }
      if (selectedPlan?.contract_id) payload.contract_id = selectedPlan.contract_id
      if (selectedPlan?.plan_id) payload.plan_id = selectedPlan.plan_id
      if (selectedPriorTx.length > 0) payload.prior_treatments = selectedPriorTx
      if (prevApproved && prevPayer) payload.previous_pa_payer = prevPayer
      // Include doctor profile info
      if (selectedDoctor) {
        payload.provider_name = selectedDoctor.name
        payload.provider_npi = selectedDoctor.npi
        payload.provider_specialty = selectedDoctor.specialty
        if (selectedDoctor.dea) payload.provider_dea = selectedDoctor.dea
        payload.practice_name = selectedDoctor.practiceName
        if (activeLocation) {
          payload.practice_phone = activeLocation.phone
          payload.practice_fax = activeLocation.fax
          payload.practice_address = activeLocation.address
        }
      }



      const res = await api.post<LMNResult & { usage_alert?: UsageAlert }>('/api/generate-lmn', payload)
      setResult(res.data)
      setEditedLetter(res.data.letter_text)
      setActiveTab('letter')
      setWhatsNextCollapsed(false)

      // Show usage alert if returned by backend
      if (res.data.usage_alert) {
        setUsageAlert(res.data.usage_alert)
      }

      // Sync updated LMN count from backend
      try {
        const user = await apiGetMe()
        const synced = {
          lmnCount: user.lmn_count,
          trialActive: user.trial_active,
          billingStarted: user.billing_started,
          monthlyLmnCount: user.monthly_lmn_count,
          monthlyLmnLimit: user.monthly_lmn_limit,
          bonusLmnCount: user.bonus_lmn_count || 0,
          bonusLmnUsed: user.bonus_lmn_used || 0,
          currentMonth: user.current_month,
        }
        updateUserData(synced)
        setUserData(prev => ({ ...prev, ...synced }))
      } catch {
        // Fallback: increment locally
        const newCount = userData.lmnCount + 1
        const newMonthly = userData.monthlyLmnCount + 1
        const newTrialActive = newCount < userData.trialLmnLimit
        updateUserData({ lmnCount: newCount, monthlyLmnCount: newMonthly, trialActive: newTrialActive || userData.billingStarted })
        setUserData(prev => ({ ...prev, lmnCount: newCount, monthlyLmnCount: newMonthly, trialActive: newTrialActive || prev.billingStarted }))
      }
    } catch (e: unknown) {
      const msg = getApiErrorMessage(e, 'Generation failed')
      setError(String(msg))
    } finally { setLoading(false) }
  }

  // ── Bulk Generation ─────────────────────────────────────────────────────────
  const handleBulkGenerate = async () => {
    const validRows = bulkRows.filter(r => r.carrier && r.plan_type && r.icd10_codes.length > 0 && r.denied_product && r.denial_reason)
    if (validRows.length === 0) {
      setBulkError('Please fill in at least one complete row (carrier, plan type, diagnosis, drug, and denial reason are required).')
      return
    }
    setBulkLoading(true)
    setBulkError(null)
    setBulkResults(null)
    setBulkProgress({ current: 0, total: validRows.length })
    try {
      const res = await apiBulkGenerate(validRows)
      setBulkResults({ total: res.total, successful: res.successful, failed: res.failed, results: res.results })
      // Sync user data
      try {
        const user = await apiGetMe()
        const synced = {
          lmnCount: user.lmn_count,
          trialActive: user.trial_active,
          billingStarted: user.billing_started,
          monthlyLmnCount: user.monthly_lmn_count,
          monthlyLmnLimit: user.monthly_lmn_limit,
          bonusLmnCount: user.bonus_lmn_count || 0,
          bonusLmnUsed: user.bonus_lmn_used || 0,
          currentMonth: user.current_month,
        }
        updateUserData(synced)
        setUserData(prev => ({ ...prev, ...synced }))
      } catch { /* fallback: local counts already updated by backend */ }
      if (res.usage_alert) setUsageAlert(res.usage_alert)
    } catch (e: unknown) {
      const msg = getApiErrorMessage(e, 'Bulk generation failed')
      setBulkError(String(msg))
    } finally {
      setBulkLoading(false)
      setBulkProgress(null)
    }
  }

  const updateBulkRow = (idx: number, field: keyof BulkLMNItemRequest, value: string | string[]) => {
    setBulkRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r))
  }

  const addBulkRow = () => {
    if (bulkRows.length >= 25) return
    setBulkRows(prev => [...prev, emptyBulkRow()])
  }

  const removeBulkRow = (idx: number) => {
    if (bulkRows.length <= 1) return
    setBulkRows(prev => prev.filter((_, i) => i !== idx))
  }

  const handleBulkDownloadAll = async () => {
    if (demoMode) {
      toast.info('Download is disabled in demo mode. Sign up for a free trial to generate and export letters!')
      return
    }
    if (!bulkResults) return
    const successIds = bulkResults.results.filter(r => r.status === 'success' && r.record_id).map(r => r.record_id!)
    for (const id of successIds) {
      try {
        const detail = await api.get(`/api/lmn-history/${id}`)
        const record = detail.data
        const res = await api.post('/api/export/docx', {
          letter_text: record.letter_text,
          drug_name: record.denied_product,
          carrier: record.carrier,
          diagnosis_codes: record.icd10_codes || [],
          citations: record.citations || [],
        }, { responseType: 'blob' })
        const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `LMN_${(record.denied_product || 'letter').replace(/\s+/g, '_')}_${(record.carrier || '').replace(/\s+/g, '_')}.docx`
        a.click()
        URL.revokeObjectURL(url)
      } catch { /* skip failed downloads */ }
    }
  }

  // When switching to bulk mode, pre-populate first row from current form values
  const handleSwitchToBulk = () => {
    const firstRow: BulkLMNItemRequest = {
      carrier: carrier || '',
      plan_type: planType || '',
      icd10_codes: selectedIcd10.map(c => c.code),
      denied_product: deniedProduct || '',
      denial_reason: denialReason || '',
      reference_tag: '',
    }
    const hasValues = firstRow.carrier || firstRow.denied_product || firstRow.icd10_codes.length > 0
    setBulkRows(hasValues ? [firstRow] : [emptyBulkRow()])
    setBulkResults(null)
    setBulkError(null)
    setFormMode('bulk')
  }

  const bulkEligible = userData.plan === 'professional_plus' || userData.plan === 'enterprise'

  const handleCopy = () => {
    if (demoMode) {
      toast.info('Copy is disabled in demo mode. Sign up for a free trial to generate and export letters!')
      return
    }
    navigator.clipboard.writeText(editedLetter)
    triggerCopied()
  }

  const handleDownload = async (type: 'txt' | 'docx' | 'pdf') => {
    if (demoMode) {
      toast.info('Download is disabled in demo mode. Sign up for a free trial to generate and export letters!')
      return
    }
    if (type === 'txt') {
      const blob = new Blob([editedLetter], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url
      a.download = `LMN_${deniedProduct.replace(/\s+/g,'_')}_${carrier.replace(/\s+/g,'_')}.txt`
      a.click(); URL.revokeObjectURL(url)
      return
    }

    try {
      const res = await api.post(`/api/export/${type}`, {
        letter_text: editedLetter,
        drug_name: deniedProduct,
        carrier: carrier,
        diagnosis_codes: selectedIcd10.map(s => s.code),
        citations: result?.citations || [],
      }, { responseType: 'blob' })

      const mimeType = type === 'docx'
        ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        : 'application/pdf'
      const blob = new Blob([res.data], { type: mimeType })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url
      const ext = type === 'docx' ? 'docx' : 'pdf'
      a.download = `LMN_${deniedProduct.replace(/\s+/g,'_')}_${carrier.replace(/\s+/g,'_')}.${ext}`
      a.click(); URL.revokeObjectURL(url)
    } catch (e) {
      /* export failed — silently ignore */
    }
  }

  const handleGenerateP2P = async () => {
    if (!carrier || !deniedProduct || !denialReason || icd10Codes.length === 0) return
    setP2pLoading(true)
    setP2pData(null)
    setP2pText('')
    setP2pHtml('')
    setShowP2pModal(true)
    setP2pExpandedSections({ opening: true, talking_points: true, objections: true, clinical: false, carrier_tips: false, closing: true, unsuccessful: false })
    try {
      const payload: Record<string, unknown> = {
        carrier,
        drug: deniedProduct,
        denial_reason: denialReason,
        icd10_codes: icd10Codes,
        plan_type: planType || 'commercial',
      }
      if (selectedPriorTx.length > 0) payload.prior_treatments = selectedPriorTx
      if (selectedDoctor) {
        payload.provider_info = {
          name: selectedDoctor.name,
          npi: selectedDoctor.npi,
          specialty: selectedDoctor.specialty,
        }
      }
      const res = await apiGenerateP2PStandalone(payload as Parameters<typeof apiGenerateP2PStandalone>[0])
      setP2pData(res.p2p_data)
      setP2pText(res.text)
      setP2pHtml(res.html)
    } catch {
      toast.error('Failed to generate P2P prep sheet')
      setShowP2pModal(false)
    }
    setP2pLoading(false)
  }

  const toggleP2pSection = (section: string) => {
    setP2pExpandedSections(prev => ({ ...prev, [section]: !prev[section] }))
  }

  const handlePrintP2P = () => {
    if (demoMode) {
      toast.info('Print is disabled in demo mode. Sign up for a free trial to access P2P prep sheets!')
      return
    }
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(`<!DOCTYPE html><html><head><title>P2P Prep Sheet</title><style>
        @media print { body { margin: 0; } @page { margin: 1cm; } }
      </style></head><body>${p2pHtml}</body></html>`)
      printWindow.document.close()
      printWindow.focus()
      setTimeout(() => printWindow.print(), 300)
    }
  }

  const handleDownloadP2P = () => {
    if (demoMode) {
      toast.info('Download is disabled in demo mode. Sign up for a free trial to access P2P prep sheets!')
      return
    }
    if (!p2pText) return
    const blob = new Blob([p2pText], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `P2P_Prep_${deniedProduct.replace(/\s+/g, '_')}_${carrier.replace(/\s+/g, '_')}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Global keyboard handler — Escape closes modals + power-user shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape — close any open modal/dropdown
      if (e.key === 'Escape') {
        if (showShortcutsModal) { setShowShortcutsModal(false); return }
        if (showSupportModal) { setShowSupportModal(false); return }
        if (showAccountSettings) { setShowAccountSettings(false); return }
        if (showPayerDirectory) { setShowPayerDirectory(false); return }
        if (showAppealsDashboard) { setShowAppealsDashboard(false); return }
        if (showDoctorModal) { setShowDoctorModal(false); return }
        return
      }

      const mod = e.metaKey || e.ctrlKey
      if (!mod) return

      // Skip shortcuts when user is typing in an input or textarea
      const tag = (e.target as HTMLElement)?.tagName
      const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'

      // Cmd/Ctrl + / — Toggle keyboard shortcuts help modal (always works)
      if (e.key === '/') {
        e.preventDefault()
        setShowShortcutsModal(prev => !prev)
        return
      }

      // Cmd/Ctrl + K — Focus the drug search input (always works)
      if (e.key === 'k') {
        e.preventDefault()
        const drugInput = document.getElementById('drug-search-input') as HTMLInputElement | null
        if (drugInput) { drugInput.focus(); drugInput.select() }
        return
      }

      // Skip remaining shortcuts if user is typing in a field
      if (isTyping) return

      // Cmd/Ctrl + G — Trigger "Generate Letter"
      if (e.key === 'g' && !e.shiftKey) {
        e.preventDefault()
        if (!trialExpired && !monthlyLimitReached && canSubmit && !loading) {
          handleGenerate()
        }
        return
      }

      // Cmd/Ctrl + D — Download generated letter as Word doc
      if (e.key === 'd' && !e.shiftKey) {
        e.preventDefault()
        if (result) { handleDownload('docx') }
        return
      }

      // Cmd/Ctrl + Shift + A — Toggle Appeals Dashboard
      if (e.key === 'A' && e.shiftKey) {
        e.preventDefault()
        setShowAppealsDashboard(prev => !prev)
        return
      }

      // Cmd/Ctrl + Shift + P — Toggle Payer Directory
      if (e.key === 'P' && e.shiftKey) {
        e.preventDefault()
        setShowPayerDirectory(prev => !prev)
        return
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [showSupportModal, showAccountSettings, showAppealsDashboard, showPayerDirectory, showDoctorModal, showShortcutsModal, trialExpired, monthlyLimitReached, canSubmit, loading, result])

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">

      {/* ── Header ── */}
      {demoMode && (
        <div className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-center py-2 px-4 text-sm font-medium">
          <span className="mr-2">You're viewing RxCheckUp in demo mode.</span>
          <button onClick={() => navigate('/signup')} className="underline font-bold hover:text-teal-100 transition-colors">Create your free account</button>
          <span className="mx-2">to generate real LMNs.</span>
          <button onClick={() => navigate('/')} className="text-teal-100 hover:text-white text-xs ml-2 underline">← Back to home</button>
        </div>
      )}
      <header className="bg-white border-b border-slate-200 dark:bg-slate-800 dark:border-slate-700">
        <div className="max-w-[1400px] mx-auto px-8 h-16 flex items-center">
          <div className="flex-shrink-0">
            <Logo subtitle="LMN Generator" />
          </div>
          <div className="flex-1 flex items-center justify-evenly ml-6">
            {/* Admin Link */}
            {userData.isAdmin && (
              <a href="/admin" className="flex items-center gap-1.5 text-xs bg-violet-50 border border-violet-200 text-violet-700 font-semibold rounded-full px-3 py-1.5 hover:bg-violet-100 transition-colors">
                <span>⚙</span> Admin
              </a>
            )}
            {/* Doctor Selector */}
            <div className="flex items-center gap-2">
              {selectedDoctor ? (
                <button onClick={() => setShowDoctorManager(true)}
                  className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-100 transition-colors">
                  <span className="w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center text-[10px] font-bold">
                    {selectedDoctor.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </span>
                  <span className="font-semibold text-slate-700 dark:text-slate-200 max-w-[140px] truncate">{selectedDoctor.name}</span>
                  <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>
              ) : (
                <button onClick={() => { setEditingDoctor(null); setShowDoctorModal(true) }}
                  className="flex items-center gap-1.5 text-xs bg-teal-50 border border-teal-200 text-teal-700 rounded-full px-3 py-1.5 hover:bg-teal-100 transition-colors font-semibold">
                  <span>+</span> Add Doctor Profile
                </button>
              )}
            </div>

            {/* Appeals Tracker button */}
            <button onClick={() => setShowAppealsDashboard(true)}
              className="flex items-center gap-1.5 text-xs font-semibold bg-teal-50 border border-teal-200 text-teal-600 rounded-full px-3 py-1.5 hover:bg-teal-100 transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
              </svg>
              Appeals
            </button>

            {/* Payer Directory button */}
            <button onClick={() => setShowPayerDirectory(true)}
              className="flex items-center gap-1.5 text-xs font-semibold bg-amber-50 border border-amber-200 text-amber-600 rounded-full px-3 py-1.5 hover:bg-amber-100 transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
              </svg>
              Payer Directory
            </button>

            {/* Market Intel button — admin-only */}
            {userData.isAdmin && (
              <button onClick={() => navigate('/market-intel')}
                className="flex items-center gap-1.5 text-xs font-semibold bg-emerald-50 border border-emerald-200 text-emerald-600 rounded-full px-3 py-1.5 hover:bg-emerald-100 transition-colors">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
                </svg>
                Market Intel
              </button>
            )}

            {/* Referral button */}
            <button onClick={() => setShowReferralPanel(true)}
              className="flex items-center gap-1.5 text-xs font-semibold bg-violet-50 border border-violet-200 text-violet-600 rounded-full px-3 py-1.5 hover:bg-violet-100 transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21" />
              </svg>
              Refer &amp; Earn
            </button>

            {/* Support / Help button */}
            <button onClick={() => { setSupportSuccess(false); setSupportError(null); setShowSupportModal(true) }}
              className="flex items-center gap-1.5 text-xs font-semibold bg-slate-50 border border-slate-200 text-slate-500 rounded-full px-3 py-1.5 hover:bg-slate-100 transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
              </svg>
              Help
            </button>

            {/* Dark Mode Toggle */}
            <button
              onClick={() => setDarkMode(prev => !prev)}
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700 transition"
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? (
                <svg className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-slate-500" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clipRule="evenodd" />
                </svg>
              )}
            </button>

            {/* Notification Bell */}
            <NotificationCenter />

            <div className="flex items-center gap-2 text-xs text-emerald-600 font-semibold bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
              No PHI Stored
            </div>

            {/* Settings */}
            <button onClick={handleOpenAccountSettings}
              className="flex items-center gap-1.5 text-xs font-semibold bg-slate-50 border border-slate-200 text-slate-500 rounded-full px-3 py-1.5 hover:bg-slate-100 transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Settings
            </button>

            <button onClick={handleLogout}
              className="text-xs text-slate-400 hover:text-slate-600 font-medium transition-colors">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* ── Free Trial Banner ── */}
      {isInTrial && !trialExpired && trialRemaining <= 3 && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-center py-2 px-4 text-sm font-medium">
          <span className="font-bold">{trialRemaining} free LMN{trialRemaining !== 1 ? 's' : ''} remaining.</span>
          {' '}Add a payment method to keep generating letters after your trial.
          <button onClick={() => setShowBillingModal(true)} className="ml-3 underline font-bold hover:no-underline">Add Payment</button>
        </div>
      )}
      {trialExpired && (
        <div className="bg-gradient-to-r from-red-500 to-rose-500 text-white text-center py-3 px-4 text-sm font-medium">
          <span className="font-bold">Your free trial has ended.</span>
          {' '}You've used all 5 free LMNs. Add a payment method to continue.
          <button onClick={() => setShowBillingModal(true)}
            className="ml-3 bg-white text-red-600 px-4 py-1 rounded-full font-bold text-xs hover:bg-red-50 transition-colors">
            Start {getUserPlan() === 'starter' ? '$149' : getUserPlan() === 'professional' ? '$299' : '$399'}/mo Plan
          </button>
        </div>
      )}

      {/* ── Monthly Limit Reached Banner ── */}
      {monthlyLimitReached && (
        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-center py-3 px-4 text-sm font-medium">
          <span className="font-bold">Monthly limit reached.</span>
          {' '}You've used all {userData.monthlyLmnLimit} LMNs for this month on your {userData.plan === 'professional_plus' ? 'Professional+' : userData.plan} plan.
          {userData.plan === 'starter' && (
            <button onClick={() => setShowBillingModal(true)}
              className="ml-3 bg-white text-red-600 px-4 py-1 rounded-full font-bold text-xs hover:bg-red-50 transition-colors">
              Upgrade to Professional — 200 LMNs/mo
            </button>
          )}
          {userData.plan === 'professional' && (
            <button onClick={() => setShowBillingModal(true)}
              className="ml-3 bg-white text-red-600 px-4 py-1 rounded-full font-bold text-xs hover:bg-red-50 transition-colors">
              Upgrade to Professional+ — 300 LMNs/mo
            </button>
          )}
          {userData.plan === 'professional_plus' && (
            <button onClick={() => setShowBillingModal(true)}
              className="ml-3 bg-white text-red-600 px-4 py-1 rounded-full font-bold text-xs hover:bg-red-50 transition-colors">
              Upgrade to Enterprise
            </button>
          )}
          {userData.plan !== 'enterprise' && (
            <button onClick={() => setShowTopupModal(true)}
              className="ml-2 bg-white/20 hover:bg-white/30 text-white px-4 py-1 rounded-full font-bold text-xs transition-colors">
              Buy More LMNs
            </button>
          )}
          {userData.plan === 'enterprise' && (
            <span className="ml-2 opacity-80">Your LMNs reset on the 1st of next month.</span>
          )}
        </div>
      )}

      {/* ── Usage Alert Toast ── */}
      {usageAlert && (
        <div className={`text-center py-2 px-4 text-sm font-medium ${
          usageAlert.remaining <= 5 ? 'bg-gradient-to-r from-red-500 to-rose-500 text-white'
          : usageAlert.remaining <= 15 ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
          : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
        }`}>
          <span className="font-bold">{usageAlert.remaining} LMN{usageAlert.remaining !== 1 ? 's' : ''} remaining this month.</span>
          {usageAlert.remaining <= 10 && usageAlert.plan !== 'enterprise' && (
            <button onClick={() => { setUsageAlert(null); setShowBillingModal(true) }}
              className="ml-3 bg-white/20 hover:bg-white/30 px-3 py-0.5 rounded-full text-xs font-bold transition-colors">
              {usageAlert.plan === 'starter' ? 'Upgrade to Professional' : usageAlert.plan === 'professional' ? 'Upgrade to Professional+' : 'Upgrade to Enterprise'}
            </button>
          )}
          <button onClick={() => setUsageAlert(null)} className="ml-3 text-white/60 hover:text-white font-bold">&times;</button>
        </div>
      )}

      {/* ── Top-Up Packs Modal ── */}
      {showTopupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
          onClick={() => setShowTopupModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/5 rounded-full" />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold">Buy More LMNs</h3>
                  <button onClick={() => setShowTopupModal(false)} className="text-white/60 hover:text-white text-lg font-bold">&times;</button>
                </div>
                <p className="text-sm text-amber-100">
                  One-time top-up packs added to your current month's allowance.
                </p>
              </div>
            </div>
            <div className="p-6 space-y-3">
              {[
                { id: 'small', lmns: 10, price: '$29.99', label: '10 Extra LMNs' },
                { id: 'medium', lmns: 25, price: '$59.99', label: '25 Extra LMNs', badge: 'Best Value' },
                { id: 'large', lmns: 50, price: '$99.99', label: '50 Extra LMNs' },
              ].map(pack => (
                <button
                  key={pack.id}
                  disabled={topupLoading !== null}
                  onClick={async () => {
                    setTopupLoading(pack.id)
                    try {
                      const res = await apiBuyTopup(pack.id) as { checkout_url?: string; session_id?: string }
                      if (res.checkout_url) {
                        window.location.href = res.checkout_url
                        return
                      }
                    } catch (e: unknown) {
                      const msg = getApiErrorMessage(e, 'Failed to purchase top-up')
                      toast.error(String(msg))
                    } finally {
                      setTopupLoading(null)
                    }
                  }}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all hover:border-amber-400 hover:bg-amber-50/30 relative ${
                    topupLoading === pack.id ? 'opacity-50' : ''
                  } ${pack.badge ? 'border-amber-300 bg-amber-50/20' : 'border-slate-200'}`}
                >
                  {pack.badge && (
                    <span className="absolute -top-2 right-4 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">{pack.badge}</span>
                  )}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-slate-800">{pack.label}</div>
                      <div className="text-xs text-slate-500 mt-0.5">+{pack.lmns} LMNs added to this month</div>
                    </div>
                    <div className="text-lg font-extrabold text-amber-600">{pack.price}</div>
                  </div>
                </button>
              ))}
              <p className="text-[11px] text-slate-400 text-center mt-2">
                Bonus LMNs are valid for the current billing month and do not roll over.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Referral Panel ── */}
      {showReferralPanel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
          onClick={() => setShowReferralPanel(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white p-6 pb-8 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/5 rounded-full" />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold">Refer & Earn</h3>
                  <button onClick={() => setShowReferralPanel(false)} className="text-white/60 hover:text-white text-lg font-bold">&times;</button>
                </div>
                <p className="text-sm text-violet-100">
                  Share your code with colleagues. When they sign up and pay for at least one month, <strong className="text-white">you get one month free</strong>.
                </p>
              </div>
            </div>

            <div className="p-6 space-y-5 -mt-4">
              {/* Referral code */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2">Your Referral Code</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-white border border-slate-200 rounded-lg px-4 py-2.5 font-mono font-bold text-slate-800 tracking-wider text-sm">
                    {userData.referralCode || <span className="text-slate-400 italic font-normal">Loading...</span>}
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(userData.referralCode || '')
                      triggerReferralCopied()
                    }}
                    className="px-4 py-2.5 bg-violet-600 text-white rounded-lg text-xs font-bold hover:bg-violet-700 transition-colors flex-shrink-0">
                    {referralCopied ? '✓ Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}/signup?ref=${userData.referralCode}`
                      navigator.clipboard.writeText(url)
                      triggerReferralCopied()
                    }}
                    className="flex-1 py-2 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors text-center">
                    Copy Signup Link
                  </button>
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}/signup?ref=${userData.referralCode}`
                      const subject = encodeURIComponent('Try RxCheckUp - LMN Generator for Prior Auth Appeals')
                      const body = encodeURIComponent(`Hey,\n\nI've been using RxCheckUp to generate Letters of Medical Necessity for prior auth appeals and it's been a huge time saver.\n\nYour first 5 LMNs are free — try it out:\n${url}\n\nUse my referral code: ${userData.referralCode}`)
                      window.open(`mailto:?subject=${subject}&body=${body}`)
                    }}
                    className="flex-1 py-2 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors text-center">
                    Share via Email
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-violet-50 rounded-xl p-3 text-center border border-violet-100">
                  <p className="text-2xl font-extrabold text-violet-700">{userData.referralCount}</p>
                  <p className="text-[10px] text-violet-500 font-semibold mt-0.5">Referrals</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-100">
                  <p className="text-2xl font-extrabold text-emerald-700">{userData.referralPaidCount}</p>
                  <p className="text-[10px] text-emerald-500 font-semibold mt-0.5">Paid</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
                  <p className="text-2xl font-extrabold text-amber-700">{userData.freeMonthsEarned - userData.freeMonthsUsed}</p>
                  <p className="text-[10px] text-amber-500 font-semibold mt-0.5">Free Months</p>
                </div>
              </div>

              {/* How it works */}
              <div className="border-t border-slate-100 pt-4">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">How It Works</p>
                <div className="space-y-2.5">
                  <div className="flex items-start gap-2.5 text-xs text-slate-600">
                    <span className="w-5 h-5 rounded-md bg-violet-100 text-violet-600 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">1</span>
                    <span>Share your referral code or signup link with a colleague</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-600">
                    <span className="w-5 h-5 rounded-md bg-violet-100 text-violet-600 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">2</span>
                    <span>They sign up using your code and get their own 5 free LMNs</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-600">
                    <span className="w-5 h-5 rounded-md bg-violet-100 text-violet-600 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">3</span>
                    <span>Once they pay for at least their first month, <strong>you get one month free</strong></span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-600">
                    <span className="w-5 h-5 rounded-md bg-violet-100 text-violet-600 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">&#8734;</span>
                    <span>No limit — refer 12 colleagues and get a full year of RxCheckUp free</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Payment Failure Banner ── */}
      {userData.paymentFailed && userData.billingStarted && (
        <div className="bg-red-600 text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span className="text-sm font-medium">Your last payment failed. Please update your payment method to avoid losing access.</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleRetryPayment} disabled={billingLoading}
              className="px-4 py-1.5 bg-white text-red-600 text-sm font-semibold rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50">
              {billingLoading ? 'Retrying...' : 'Retry Payment'}
            </button>
            <button onClick={handleManageBilling}
              className="px-4 py-1.5 bg-red-500 text-white text-sm font-semibold rounded-lg hover:bg-red-400 transition-colors">
              Update Card
            </button>
          </div>
        </div>
      )}

      {/* ── Cancellation Pending Banner ── */}
      {userData.cancelAtPeriodEnd && userData.billingStarted && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-medium text-amber-800">
              Your subscription ends {userData.subscriptionEndsAt ? new Date(userData.subscriptionEndsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'at end of billing period'}.
            </span>
          </div>
          <button onClick={handleReactivate} disabled={billingLoading}
            className="px-4 py-1.5 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50">
            {billingLoading ? 'Processing...' : 'Keep Subscription'}
          </button>
        </div>
      )}

      {/* ── Billing / Upgrade Modal ── */}
      {showBillingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
          onClick={() => { setShowBillingModal(false); setBillingError(null); setBillingSuccess(null); setShowCancelConfirm(false) }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white p-6 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/5 rounded-full" />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold">{userData.billingStarted ? 'Manage Subscription' : 'Choose Your Plan'}</h3>
                  <button onClick={() => { setShowBillingModal(false); setBillingError(null); setBillingSuccess(null) }} className="text-white/60 hover:text-white text-lg font-bold">&times;</button>
                </div>
                <p className="text-sm text-teal-100">
                  {userData.billingStarted
                    ? `${userData.plan === 'professional_plus' ? 'Professional+' : userData.plan.charAt(0).toUpperCase() + userData.plan.slice(1)} Plan · ${monthlyRemaining} LMNs remaining this month`
                    : 'Start generating LMNs with a paid subscription.'}
                </p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {billingError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">{billingError}</div>
              )}
              {billingSuccess && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-600">{billingSuccess}</div>
              )}

              {/* Billing Period Toggle */}
              <div className="flex items-center justify-center gap-1 bg-slate-100 rounded-xl p-1">
                <button
                  onClick={() => setBillingPeriod('monthly')}
                  className={`flex-1 py-2 px-4 rounded-lg text-sm font-semibold transition-all ${
                    billingPeriod === 'monthly' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingPeriod('annual')}
                  className={`flex-1 py-2 px-4 rounded-lg text-sm font-semibold transition-all ${
                    billingPeriod === 'annual' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Annual (Save 20%)
                </button>
              </div>

              {/* Plan Cards — always shown for switching/subscribing */}
              <div className="grid grid-cols-4 gap-3">
                {/* Starter Plan */}
                <div className={`border-2 rounded-2xl p-4 relative transition-all ${
                  userData.plan === 'starter' && userData.billingStarted ? 'border-teal-500 bg-teal-50/30 ring-2 ring-teal-200' : 'border-teal-200'
                }`}>
                  {userData.plan === 'starter' && userData.billingStarted && (
                    <div className="absolute -top-2.5 left-4 bg-teal-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase">Current</div>
                  )}
                  <div className="text-[10px] font-bold text-teal-600 uppercase tracking-wider mb-2">Starter</div>
                  <div className="text-2xl font-extrabold text-slate-800">${billingPeriod === 'annual' ? '119' : '149'}<span className="text-xs font-medium text-slate-400">/mo</span></div>
                  {billingPeriod === 'annual' && (
                    <div className="text-[10px] text-teal-600 font-medium">Billed annually ($1,428/yr)</div>
                  )}
                  <ul className="mt-3 space-y-1.5 text-xs text-slate-600">
                    <li className="flex items-center gap-1.5"><span className="text-teal-500">&#10003;</span> 50 LMNs/month</li>
                    <li className="flex items-center gap-1.5"><span className="text-teal-500">&#10003;</span> 3 doctors</li>
                    <li className="flex items-center gap-1.5"><span className="text-teal-500">&#10003;</span> PDF & DOCX export</li>
                    <li className="flex items-center gap-1.5"><span className="text-teal-500">&#10003;</span> Appeals tracking</li>
                  </ul>
                  {userData.plan === 'starter' && userData.billingStarted ? (
                    <div className="mt-4 w-full py-2 rounded-xl font-semibold text-xs bg-teal-100 text-teal-700 text-center">Active</div>
                  ) : (
                    <button onClick={() => handleUpgrade('starter')} disabled={billingLoading}
                      className="mt-4 w-full py-2 rounded-xl font-semibold text-xs bg-teal-500 hover:bg-teal-600 text-white transition-colors disabled:opacity-50">
                      {billingLoading ? 'Processing...' : userData.billingStarted ? 'Switch to Starter' : 'Subscribe'}
                    </button>
                  )}
                </div>

                {/* Professional Plan */}
                <div className={`border-2 rounded-2xl p-4 relative transition-all ${
                  userData.plan === 'professional' && userData.billingStarted ? 'border-blue-500 bg-blue-50/30 ring-2 ring-blue-200' : 'border-blue-200 bg-blue-50/30'
                }`}>
                  {userData.plan === 'professional' && userData.billingStarted ? (
                    <div className="absolute -top-2.5 left-4 bg-blue-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase">Current</div>
                  ) : (
                    <div className="absolute -top-2.5 right-3 bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">Popular</div>
                  )}
                  <div className="text-[10px] font-bold text-blue-600 uppercase tracking-wider mb-2">Professional</div>
                  <div className="text-2xl font-extrabold text-slate-800">${billingPeriod === 'annual' ? '239' : '299'}<span className="text-xs font-medium text-slate-400">/mo</span></div>
                  {billingPeriod === 'annual' && (
                    <div className="text-[10px] text-blue-600 font-medium">Billed annually ($2,868/yr)</div>
                  )}
                  <ul className="mt-3 space-y-1.5 text-xs text-slate-600">
                    <li className="flex items-center gap-1.5"><span className="text-blue-500">&#10003;</span> 200 LMNs/month</li>
                    <li className="flex items-center gap-1.5"><span className="text-blue-500">&#10003;</span> 8 doctors</li>
                    <li className="flex items-center gap-1.5"><span className="text-blue-500">&#10003;</span> Approval prediction</li>
                    <li className="flex items-center gap-1.5"><span className="text-blue-500">&#10003;</span> P2P prep sheets</li>
                  </ul>
                  {userData.plan === 'professional' && userData.billingStarted ? (
                    <div className="mt-4 w-full py-2 rounded-xl font-semibold text-xs bg-blue-100 text-blue-700 text-center">Active</div>
                  ) : (
                    <button onClick={() => handleUpgrade('professional')} disabled={billingLoading}
                      className="mt-4 w-full py-2 rounded-xl font-semibold text-xs bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50">
                      {billingLoading ? 'Processing...' : userData.billingStarted ? 'Upgrade to Professional' : 'Subscribe'}
                    </button>
                  )}
                </div>

                {/* Professional+ Plan */}
                <div className={`border-2 rounded-2xl p-4 relative transition-all ${
                  userData.plan === 'professional_plus' && userData.billingStarted ? 'border-indigo-500 bg-indigo-50/30 ring-2 ring-indigo-200' : 'border-indigo-200'
                }`}>
                  {userData.plan === 'professional_plus' && userData.billingStarted && (
                    <div className="absolute -top-2.5 left-4 bg-indigo-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase">Current</div>
                  )}
                  <div className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-2">Professional+</div>
                  <div className="text-2xl font-extrabold text-slate-800">${billingPeriod === 'annual' ? '319' : '399'}<span className="text-xs font-medium text-slate-400">/mo</span></div>
                  {billingPeriod === 'annual' && (
                    <div className="text-[10px] text-indigo-600 font-medium">Billed annually ($3,828/yr)</div>
                  )}
                  <ul className="mt-3 space-y-1.5 text-xs text-slate-600">
                    <li className="flex items-center gap-1.5"><span className="text-indigo-500">&#10003;</span> 300 LMNs/month</li>
                    <li className="flex items-center gap-1.5"><span className="text-indigo-500">&#10003;</span> Smart alerts</li>
                    <li className="flex items-center gap-1.5"><span className="text-indigo-500">&#10003;</span> Community intelligence</li>
                    <li className="flex items-center gap-1.5"><span className="text-indigo-500">&#10003;</span> ROI dashboard</li>
                  </ul>
                  {userData.plan === 'professional_plus' && userData.billingStarted ? (
                    <div className="mt-4 w-full py-2 rounded-xl font-semibold text-xs bg-indigo-100 text-indigo-700 text-center">Active</div>
                  ) : (
                    <button onClick={() => handleUpgrade('professional_plus')} disabled={billingLoading}
                      className="mt-4 w-full py-2 rounded-xl font-semibold text-xs bg-indigo-600 hover:bg-indigo-700 text-white transition-colors disabled:opacity-50">
                      {billingLoading ? 'Processing...' : userData.billingStarted ? 'Upgrade to Professional+' : 'Subscribe'}
                    </button>
                  )}
                </div>

                {/* Enterprise Plan */}
                <div className={`border-2 rounded-2xl p-4 relative transition-all ${
                  userData.plan === 'enterprise' && userData.billingStarted ? 'border-violet-500 bg-violet-50/30 ring-2 ring-violet-200' : 'border-violet-200'
                }`}>
                  {userData.plan === 'enterprise' && userData.billingStarted && (
                    <div className="absolute -top-2.5 left-4 bg-violet-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase">Current</div>
                  )}
                  <div className="text-[10px] font-bold text-violet-600 uppercase tracking-wider mb-2">Enterprise</div>
                  <div className="text-2xl font-extrabold text-slate-800">Custom</div>
                  <div className="text-[10px] text-violet-600 font-medium">Tailored to your organization</div>
                  <ul className="mt-3 space-y-1.5 text-xs text-slate-600">
                    <li className="flex items-center gap-1.5"><span className="text-violet-500">&#10003;</span> Unlimited LMNs</li>
                    <li className="flex items-center gap-1.5"><span className="text-violet-500">&#10003;</span> Unlimited doctors</li>
                    <li className="flex items-center gap-1.5"><span className="text-violet-500">&#10003;</span> Custom branding</li>
                    <li className="flex items-center gap-1.5"><span className="text-violet-500">&#10003;</span> Dedicated support</li>
                  </ul>
                  {userData.plan === 'enterprise' && userData.billingStarted ? (
                    <div className="mt-4 w-full py-2 rounded-xl font-semibold text-xs bg-violet-100 text-violet-700 text-center">Active</div>
                  ) : (
                    <a href={`mailto:info@rxcheckup.ai?subject=${encodeURIComponent('Enterprise Plan Inquiry')}&body=${encodeURIComponent(`Hi,\n\nI'm interested in the Enterprise plan for RxCheckUp.\n\nName: ${userData.name || ''}\nEmail: ${userData.email || ''}\nCurrent Plan: ${userData.plan || 'N/A'}\n\nPlease send me more information.\n\nThank you`)}`}
                      className="mt-4 w-full py-2 rounded-xl font-semibold text-xs bg-violet-600 hover:bg-violet-700 text-white transition-colors text-center block">
                      Contact Sales
                    </a>
                  )}
                </div>
              </div>

              {userData.billingStarted && !['professional_plus', 'enterprise'].includes(userData.plan) && (
                <p className="text-center text-xs text-slate-400">Plan changes are prorated — you only pay the difference for the rest of your billing period.</p>
              )}

              {(userData.freeMonthsEarned - userData.freeMonthsUsed) > 0 && !userData.billingStarted && (
                <div className="p-3 bg-violet-50 border border-violet-200 rounded-xl text-sm text-violet-700 text-center font-medium">
                  You have {userData.freeMonthsEarned - userData.freeMonthsUsed} free month(s) from referrals — applied at checkout!
                </div>
              )}

              {/* Invoice History Section */}
              {userData.billingStarted && (
                <div className="border-t border-slate-100 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Recent Invoices</p>
                    {invoices.length === 0 && !invoicesLoading && (
                      <button onClick={loadInvoices} className="text-xs text-blue-600 font-semibold hover:text-blue-700">
                        Load Invoices
                      </button>
                    )}
                  </div>
                  {invoicesLoading ? (
                    <div className="text-center py-4 text-sm text-slate-400">Loading invoices...</div>
                  ) : invoices.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {invoices.map(inv => (
                        <div key={inv.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                          <div>
                            <p className="text-xs font-semibold text-slate-700">
                              {inv.number || 'Invoice'} — ${(inv.amount_paid / 100).toFixed(2)}
                            </p>
                            <p className="text-[10px] text-slate-400">
                              {new Date(inv.created * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              {' · '}
                              <span className={inv.status === 'paid' ? 'text-emerald-600' : inv.status === 'open' ? 'text-amber-600' : 'text-slate-400'}>
                                {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                              </span>
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {inv.hosted_invoice_url && (
                              <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-700 font-medium">View</a>
                            )}
                            {inv.invoice_pdf && (
                              <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer"
                                className="text-xs text-slate-500 hover:text-slate-700 font-medium">PDF</a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              )}

              {/* Manage / Cancel Section */}
              {userData.billingStarted && (
                <div className="border-t border-slate-100 pt-4 space-y-3">
                  <button onClick={handleManageBilling}
                    className="w-full px-4 py-2.5 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-semibold text-slate-700 transition-colors">
                    Manage Payment Method & Billing
                  </button>

                  {userData.cancelAtPeriodEnd ? (
                    <div className="text-center space-y-2">
                      <p className="text-xs text-amber-600 font-medium">
                        Cancellation scheduled — access continues until {userData.subscriptionEndsAt ? new Date(userData.subscriptionEndsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : 'end of period'}.
                      </p>
                      <button onClick={handleReactivate} disabled={billingLoading}
                        className="text-xs text-blue-600 font-semibold hover:text-blue-700">
                        Keep my subscription
                      </button>
                    </div>
                  ) : showCancelConfirm ? (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <p className="text-sm font-semibold text-red-800 mb-2">Are you sure?</p>
                      <p className="text-xs text-red-600 mb-3">
                        Your subscription will remain active until the end of your current billing period. After that, you'll lose access to LMN generation.
                      </p>
                      <div className="flex gap-2">
                        <button onClick={() => setShowCancelConfirm(false)}
                          className="flex-1 px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-50">
                          Never mind
                        </button>
                        <button onClick={handleCancelSubscription} disabled={cancelLoading}
                          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
                          {cancelLoading ? 'Cancelling...' : 'Yes, cancel'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setShowCancelConfirm(true)}
                      className="w-full text-center text-xs text-slate-400 hover:text-red-500 transition-colors">
                      Cancel subscription
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* ── Onboarding Checklist ── */}
      {!onboardingDismissed && (
        <OnboardingChecklist
          userData={userData}
          doctors={doctors}
          teamData={teamData}
          selectedDoctor={selectedDoctor}
          onDismiss={() => { setOnboardingDismissed(true); localStorage.setItem('onboarding_dismissed', 'true') }}
          onAddDoctor={() => { setEditingDoctor(null); setShowDoctorModal(true) }}
          onEditDoctor={() => { if (selectedDoctor) { setEditingDoctor(selectedDoctor); setShowDoctorModal(true) } else { setEditingDoctor(null); setShowDoctorModal(true) } }}
          onOpenAppeals={() => setShowAppealsDashboard(true)}
          onOpenTeam={() => { setAccountTab('team'); loadTeamData(); setShowAccountSettings(true) }}
        />
      )}

      {/* ── Practice Performance Summary Bar ── */}
      <PerformanceBar
        userData={userData}
        perfData={perfData}
        perfLoading={perfLoading}
        onViewReport={() => setShowAppealsDashboard(true)}
      />

      {/* ── Intelligence Hub ── */}
      <div className="max-w-[1600px] mx-auto px-6 pt-6">
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden shadow-sm">
          <button
            onClick={toggleIntelligence}
            className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" /></svg>
              </div>
              <div className="text-left">
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Intelligence Hub</h3>
                <p className="text-[11px] text-slate-400">AI-powered insights to boost approval rates</p>
              </div>
            </div>
            <svg className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${intelligenceExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
          </button>
          {intelligenceExpanded && (
            <div className="px-5 pb-5 pt-1">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Card 1: Approval Predictor */}
                <div className="relative rounded-xl border border-slate-200 dark:border-slate-600 bg-gradient-to-br from-teal-50/60 to-cyan-50/40 dark:from-teal-900/20 dark:to-cyan-900/10 p-4 overflow-hidden">
                  {!hasApprovalPredictor && (
                    <div className="absolute inset-0 bg-white/60 dark:bg-slate-800/70 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center">
                      <svg className="w-5 h-5 text-slate-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                      <span className="text-xs font-semibold text-slate-500">Professional</span>
                      <button onClick={() => { setAccountTab('subscription'); setShowAccountSettings(true) }} className="mt-1.5 text-[10px] font-bold text-teal-600 hover:text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-3 py-1 hover:bg-teal-100 transition-colors">Upgrade</button>
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-sm">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /></svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">Approval Predictor</h4>
                      <div className="text-lg font-black text-teal-600 dark:text-teal-400 leading-tight">&mdash;</div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">Get approval likelihood before you submit</p>
                </div>
                {/* Card 2: Smart Alerts */}
                <div className="relative rounded-xl border border-slate-200 dark:border-slate-600 bg-gradient-to-br from-amber-50/60 to-orange-50/40 dark:from-amber-900/20 dark:to-orange-900/10 p-4 overflow-hidden">
                  {!hasSmartAlerts && (
                    <div className="absolute inset-0 bg-white/60 dark:bg-slate-800/70 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center">
                      <svg className="w-5 h-5 text-slate-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                      <span className="text-xs font-semibold text-slate-500">Professional+</span>
                      <button onClick={() => { setAccountTab('subscription'); setShowAccountSettings(true) }} className="mt-1.5 text-[10px] font-bold text-teal-600 hover:text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-3 py-1 hover:bg-teal-100 transition-colors">Upgrade</button>
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" /></svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">Smart Alerts</h4>
                      <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400 rounded-full">0</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">Formulary changes, deadline warnings, policy updates</p>
                </div>
                {/* Card 3: Community Insights */}
                <div className="relative rounded-xl border border-slate-200 dark:border-slate-600 bg-gradient-to-br from-violet-50/60 to-purple-50/40 dark:from-violet-900/20 dark:to-purple-900/10 p-4 overflow-hidden">
                  {!hasCommunityInsights && (
                    <div className="absolute inset-0 bg-white/60 dark:bg-slate-800/70 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center">
                      <svg className="w-5 h-5 text-slate-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                      <span className="text-xs font-semibold text-slate-500">Professional+</span>
                      <button onClick={() => { setAccountTab('subscription'); setShowAccountSettings(true) }} className="mt-1.5 text-[10px] font-bold text-teal-600 hover:text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-3 py-1 hover:bg-teal-100 transition-colors">Upgrade</button>
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center shadow-sm">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" /></svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">Community Insights</h4>
                      <svg className="w-4 h-4 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22" /></svg>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{"See what's working across practices"}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="max-w-[1600px] mx-auto px-6 py-8 flex gap-6">

        {/* ── Left Panel: Input Form ── */}
        <div className="w-[400px] flex-shrink-0 space-y-5">

          {/* Demo Mode: Show static read-only summary instead of live form */}
          {demoMode && (
            <div className="space-y-5">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2.5 rounded-t-2xl">
                  <span className="w-6 h-6 rounded-md bg-teal-500 text-white text-[11px] flex items-center justify-center font-bold">1</span>
                  <span className="font-semibold text-slate-700 text-sm">Payer & Plan</span>
                  <span className="ml-auto text-[10px] bg-teal-50 text-teal-600 px-2 py-0.5 rounded-full font-bold">DEMO</span>
                </div>
                <div className="p-5 space-y-3">
                  <div><p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Insurance Carrier</p><p className="text-sm font-semibold text-slate-700">Aetna</p></div>
                  <div><p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Plan Type</p><p className="text-sm font-semibold text-slate-700">Commercial PPO</p></div>
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2.5 rounded-t-2xl">
                  <span className="w-6 h-6 rounded-md bg-blue-500 text-white text-[11px] flex items-center justify-center font-bold">2</span>
                  <span className="font-semibold text-slate-700 text-sm">Clinical Details</span>
                </div>
                <div className="p-5 space-y-3">
                  <div><p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">ICD-10 Diagnosis</p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-1 font-medium">L40.0 — Psoriasis vulgaris</span>
                      <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-1 font-medium">L40.50 — Arthropathic psoriasis</span>
                    </div>
                  </div>
                  <div><p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Denied Medication</p><p className="text-sm font-semibold text-slate-700">Skyrizi (risankizumab)</p></div>
                  <div><p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Denial Reason</p><p className="text-sm font-semibold text-slate-700">Step Therapy / Fail First</p></div>
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2.5 rounded-t-2xl">
                  <span className="w-6 h-6 rounded-md bg-violet-500 text-white text-[11px] flex items-center justify-center font-bold">3</span>
                  <span className="font-semibold text-slate-700 text-sm">Enrichment</span>
                </div>
                <div className="p-5 space-y-3">
                  <div><p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Prior Treatments</p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-xs bg-violet-50 text-violet-700 border border-violet-200 rounded-full px-2.5 py-1 font-medium">Humira (adalimumab)</span>
                      <span className="text-xs bg-violet-50 text-violet-700 border border-violet-200 rounded-full px-2.5 py-1 font-medium">Otezla (apremilast)</span>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <span className="text-xs text-slate-600 flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded bg-teal-500 text-white text-[9px] flex items-center justify-center">✓</span>Specialist involved</span>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-teal-500 to-cyan-500 rounded-2xl p-5 text-center shadow-lg shadow-teal-500/20">
                <p className="text-white font-bold text-sm mb-2">Like what you see?</p>
                <p className="text-teal-100 text-xs mb-4">Sign up for a free trial — your first 5 LMNs are on us.</p>
                <button onClick={() => navigate('/signup')} className="bg-white text-teal-600 font-bold text-sm px-6 py-2.5 rounded-xl hover:bg-teal-50 transition-colors shadow-md">
                  Start Free Trial →
                </button>
              </div>
            </div>
          )}

          {/* Saved Templates Dropdown */}
          <div className="relative" ref={savedTemplatesRef} style={demoMode ? { display: 'none' } : undefined}>
            <button
              onClick={() => setShowSavedTemplates(!showSavedTemplates)}
              className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-white border border-slate-200 hover:border-amber-300 hover:bg-amber-50/30 text-sm font-medium text-slate-600 transition-all shadow-sm dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300"
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                Saved Templates
                {savedTemplates.length > 0 && (
                  <span className="text-[10px] bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded-full font-bold">{savedTemplates.length}</span>
                )}
              </span>
              <svg className={`w-4 h-4 text-slate-400 transition-transform ${showSavedTemplates ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </button>

            {showSavedTemplates && (
              <div className="absolute z-30 mt-1.5 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
                {savedTemplates.length === 0 ? (
                  <div className="px-4 py-6 text-center">
                    <p className="text-sm text-slate-400">No saved templates yet.</p>
                    <p className="text-[11px] text-slate-300 mt-1">Generate an LMN, then save it as a template for one-click reuse.</p>
                  </div>
                ) : (
                  <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                    {savedTemplates.map(t => (
                      <div key={t.id} className="px-4 py-3 hover:bg-slate-50 transition-colors group">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1 cursor-pointer" onClick={() => handleUseSavedTemplate(t)}>
                            <p className="text-sm font-semibold text-slate-700 truncate">{t.name}</p>
                            <p className="text-[11px] text-slate-400 mt-0.5 truncate">
                              {t.denied_product} &bull; {t.carrier} &bull; {t.icd10_codes.join(', ')}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <button
                              onClick={() => handleUseSavedTemplate(t)}
                              className="text-[10px] px-2.5 py-1 rounded-lg bg-teal-50 text-teal-600 font-semibold hover:bg-teal-100 transition-colors"
                            >Use</button>
                            <button
                              onClick={(e) => handleDeleteSavedTemplate(t.id, e)}
                              className="text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 text-lg leading-none"
                              title="Delete template"
                            >&times;</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Hint: use a saved template when form is empty */}
          {savedTemplates.length > 0 && !carrier && !deniedProduct && !denialReason && selectedIcd10.length === 0 && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50/60 border border-amber-100">
              <svg className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
              <p className="text-[11px] text-amber-600">
                Have a frequent appeal? <button onClick={() => setShowSavedTemplates(true)} className="font-semibold text-amber-700 hover:underline">Use a saved template &rarr;</button>
              </p>
            </div>
          )}

          {/* Everything below hidden in demo — static summary shown above instead */}
          {!demoMode && <>
          {/* Single / Bulk Mode Switcher */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
            <button
              onClick={() => { setFormMode('single'); setBulkResults(null); setBulkError(null) }}
              className={`flex-1 text-xs font-semibold py-2 px-3 rounded-lg transition-all ${formMode === 'single' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Single
            </button>
            {bulkEligible ? (
              <button
                onClick={handleSwitchToBulk}
                className={`flex-1 text-xs font-semibold py-2 px-3 rounded-lg transition-all flex items-center justify-center gap-1.5 ${formMode === 'bulk' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" /></svg>
                Bulk
              </button>
            ) : (
              <div className="flex-1 relative group">
                <button
                  disabled
                  className="w-full text-xs font-semibold py-2 px-3 rounded-lg text-slate-300 cursor-not-allowed flex items-center justify-center gap-1.5"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                  Bulk
                </button>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-800 text-white text-[10px] rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                  Upgrade to Professional+ for bulk generation
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                </div>
              </div>
            )}
          </div>

          {/* Doctor Profile Card */}
          {formMode === 'single' && (
            <DoctorSelector
              doctors={doctors}
              selectedDoctor={selectedDoctor}
              selectedDoctorId={selectedDoctorId}
              onSelectDoctor={(id) => { setSelectedDoctorId(id); setSelectedLocationIdx(0) }}
              onEditDoctor={(doc) => { setEditingDoctor(doc); setShowDoctorModal(true) }}
              onAddDoctor={() => { setEditingDoctor(null); setShowDoctorModal(true) }}
              selectedLocationIdx={selectedLocationIdx}
              onSelectLocation={setSelectedLocationIdx}
            />
          )}

          {/* LMN Usage Bar — always visible between doctor card and Payer section */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm dark:bg-slate-800 dark:border-slate-700 px-5 py-3">
            {isInTrial && !trialExpired && (
              <>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-amber-700">Free Trial</span>
                  <span className="text-xs text-amber-600 font-medium">{trialRemaining} of {userData.trialLmnLimit} LMNs remaining</span>
                </div>
                <div className="w-full bg-amber-100 rounded-full h-2">
                  <div className="bg-amber-500 h-2 rounded-full transition-all" style={{ width: `${Math.max(2, ((userData.trialLmnLimit - trialRemaining) / userData.trialLmnLimit) * 100)}%` }} />
                </div>
              </>
            )}
            {trialExpired && (
              <button onClick={() => setShowBillingModal(true)} className="w-full flex items-center justify-center gap-2 text-sm font-bold text-red-600 hover:text-red-700 transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                Trial ended — Add payment to continue
              </button>
            )}
            {userData.billingStarted && (
              <>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-slate-700">
                    {monthlyRemaining} of {userData.monthlyLmnLimit} LMNs remaining this month
                    {bonusRemaining > 0 && <span className="text-indigo-500"> (+ {bonusRemaining} bonus)</span>}
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div className={`h-2 rounded-full transition-all ${monthlyRemaining <= 5 ? 'bg-red-500' : monthlyRemaining <= 15 ? 'bg-amber-500' : 'bg-teal-500'}`}
                    style={{ width: `${Math.max(2, ((totalMonthlyAvailable - monthlyRemaining) / totalMonthlyAvailable) * 100)}%` }} />
                </div>
              </>
            )}
          </div>

          {/* ── BULK MODE ── */}
          {formMode === 'bulk' && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="w-6 h-6 rounded-md bg-indigo-500 text-white text-[11px] flex items-center justify-center font-bold">B</span>
                    <span className="font-semibold text-slate-700 text-sm">Bulk LMN Generation</span>
                    <span className="text-[10px] bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full font-bold">{bulkRows.length}/25</span>
                  </div>
                  {bulkRows.length < 25 && (
                    <button onClick={addBulkRow}
                      className="text-xs font-semibold text-teal-600 hover:text-teal-700 flex items-center gap-1 transition-colors">
                      <span className="text-lg leading-none">+</span> Add Row
                    </button>
                  )}
                </div>
                <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                  {bulkRows.map((row, idx) => (
                    <div key={idx} className="p-4 space-y-3 relative group hover:bg-slate-50/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Row {idx + 1}</span>
                        {bulkRows.length > 1 && (
                          <button onClick={() => removeBulkRow(idx)}
                            className="text-xs text-slate-300 hover:text-red-500 font-bold transition-colors opacity-0 group-hover:opacity-100">&times; Remove</button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500 mb-0.5 block">Carrier *</label>
                          <select className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-400 bg-white"
                            value={row.carrier} onChange={e => updateBulkRow(idx, 'carrier', e.target.value)}>
                            <option value="">Select...</option>
                            {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500 mb-0.5 block">Plan Type *</label>
                          <select className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-400 bg-white"
                            value={row.plan_type} onChange={e => updateBulkRow(idx, 'plan_type', e.target.value)}>
                            <option value="">Select...</option>
                            {PLAN_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500 mb-0.5 block">Drug / Product *</label>
                          <input className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-400 bg-white placeholder:text-slate-300"
                            value={row.denied_product} onChange={e => updateBulkRow(idx, 'denied_product', e.target.value)}
                            placeholder="e.g. Humira" />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500 mb-0.5 block">Diagnosis Code *</label>
                          <input className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-400 bg-white font-mono placeholder:text-slate-300"
                            value={row.icd10_codes.join(', ')}
                            onChange={e => updateBulkRow(idx, 'icd10_codes', e.target.value.split(/[,\s]+/).map(s => s.trim()).filter(Boolean))}
                            placeholder="L40.0, M07.3" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500 mb-0.5 block">Denial Reason *</label>
                          <select className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-400 bg-white"
                            value={row.denial_reason} onChange={e => updateBulkRow(idx, 'denial_reason', e.target.value)}>
                            <option value="">Select...</option>
                            {DENIAL_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500 mb-0.5 block">Reference Tag</label>
                          <input className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-400 bg-white placeholder:text-slate-300"
                            value={row.reference_tag || ''} onChange={e => updateBulkRow(idx, 'reference_tag', e.target.value)}
                            placeholder="e.g. JD-March" maxLength={100} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bulk Generate Button */}
              <button
                onClick={handleBulkGenerate}
                disabled={bulkLoading || bulkRows.every(r => !r.carrier || !r.denied_product || !r.denial_reason || !r.plan_type || r.icd10_codes.length === 0)}
                className={`w-full py-3.5 px-6 rounded-2xl font-semibold text-sm transition-all duration-200
                  ${bulkLoading
                    ? 'bg-indigo-400 text-white cursor-wait'
                    : bulkRows.some(r => r.carrier && r.denied_product && r.denial_reason && r.plan_type && r.icd10_codes.length > 0)
                      ? 'bg-indigo-500 hover:bg-indigo-600 text-white cursor-pointer shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 active:scale-[0.99]'
                      : 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                  }`}
              >
                {bulkLoading ? (
                  <span className="flex items-center justify-center gap-2.5">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block" />
                    Processing {bulkRows.length} {bulkRows.length === 1 ? 'letter' : 'letters'}...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" /></svg>
                    Generate All ({bulkRows.filter(r => r.carrier && r.denied_product && r.denial_reason && r.plan_type && r.icd10_codes.length > 0).length} {bulkRows.filter(r => r.carrier && r.denied_product && r.denial_reason && r.plan_type && r.icd10_codes.length > 0).length === 1 ? 'letter' : 'letters'})
                    {userData.billingStarted && <span className="text-indigo-200 text-xs ml-1">({monthlyRemaining} left)</span>}
                  </span>
                )}
              </button>

              {/* Bulk Error */}
              {bulkError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600">
                  {bulkError}
                </div>
              )}

              {/* Bulk Results Summary */}
              {bulkResults && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-100">
                    <div className="flex items-center gap-2 mb-1">
                      {bulkResults.failed === 0 ? (
                        <span className="text-emerald-500 text-lg">&#10003;</span>
                      ) : (
                        <span className="text-amber-500 text-lg">&#9888;</span>
                      )}
                      <span className="text-sm font-bold text-slate-800">
                        {bulkResults.successful} of {bulkResults.total} letters generated successfully
                      </span>
                    </div>
                    {bulkResults.failed > 0 && (
                      <p className="text-xs text-slate-500">{bulkResults.failed} failed — see details below</p>
                    )}
                  </div>
                  <div className="divide-y divide-slate-100 max-h-60 overflow-y-auto">
                    {bulkResults.results.map((r) => (
                      <div key={r.index} className={`px-5 py-2.5 flex items-center justify-between text-xs ${r.status === 'error' ? 'bg-red-50/50' : ''}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          {r.status === 'success' ? (
                            <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-[10px] font-bold flex-shrink-0">&#10003;</span>
                          ) : (
                            <span className="w-5 h-5 rounded-full bg-red-100 text-red-500 flex items-center justify-center text-[10px] font-bold flex-shrink-0">&#10007;</span>
                          )}
                          <span className="font-medium text-slate-700 truncate">{r.denied_product || `Row ${r.index + 1}`}</span>
                          {r.carrier && <span className="text-slate-400 truncate">{r.carrier}</span>}
                          {r.reference_tag && <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-mono">{r.reference_tag}</span>}
                        </div>
                        {r.status === 'error' && (
                          <span className="text-red-500 text-[10px] ml-2 flex-shrink-0">{r.error}</span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="px-5 py-3 border-t border-slate-100 flex items-center gap-3">
                    {bulkResults.successful > 0 && (
                      <button onClick={handleBulkDownloadAll}
                        className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1.5 transition-colors">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6H6zm7 1.5L18.5 9H13V3.5zM8 13h8v1.5H8V13zm0 3h8v1.5H8V16zm0-6h3v1.5H8V10z"/></svg>
                        Download All ({bulkResults.successful})
                      </button>
                    )}
                    <button onClick={() => setShowAppealsDashboard(true)}
                      className="text-xs font-semibold text-slate-500 hover:text-teal-600 transition-colors">
                      View in History &rarr;
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── SINGLE MODE ── */}
          {formMode === 'single' && <>

          {/* Section 1: Payer Information */}
          <div data-tour="payer-section" className="bg-white rounded-2xl border border-slate-200 shadow-sm dark:bg-slate-800 dark:border-slate-700">
            <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2.5 rounded-t-2xl">
              <span className="w-6 h-6 rounded-md bg-teal-500 text-white text-[11px] flex items-center justify-center font-bold">1</span>
              <span className="font-semibold text-slate-700 dark:text-slate-200 text-sm">Payer & Plan</span>
            </div>
            <div className="p-5 space-y-4">
              <div data-tour="carrier-field">
                <Label>Insurance Carrier <span className="text-rose-400">*</span></Label>
                <select className={InputClass} value={carrier} onChange={e => setCarrier(e.target.value)}>
                  <option value="">Select carrier...</option>
                  {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <Label>Plan Type <span className="text-rose-400">*</span></Label>
                <select className={InputClass} value={planType} onChange={e => setPlanType(e.target.value)}>
                  <option value="">Select plan type...</option>
                  {PLAN_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>

              {carrier && (
                <div>
                  <Label>Specific Plan <span className="text-xs font-normal text-slate-400 ml-1">Recommended</span></Label>
                  <PlanSelector carrier={carrier} planType={planType} selectedPlan={selectedPlan} onSelect={setSelectedPlan}
                      onAddPlan={() => { setUploadCarrier(carrier); setUploadPlanName(''); setShowUploadModal(true) }} />
                  <p className="text-[11px] text-slate-400 mt-1.5">Enables exact formulary and policy matching.</p>
                </div>
              )}
            </div>
          </div>

          {/* Section 2: Clinical Information */}
          <div data-tour="clinical-section" className="bg-white rounded-2xl border border-slate-200 shadow-sm dark:bg-slate-800 dark:border-slate-700">
            <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2.5 rounded-t-2xl">
              <span className="w-6 h-6 rounded-md bg-blue-500 text-white text-[11px] flex items-center justify-center font-bold">2</span>
              <span className="font-semibold text-slate-700 dark:text-slate-200 text-sm">Clinical Details</span>
            </div>
            <div className="p-5 space-y-4">
              <div data-tour="icd10-field">
                <Label>ICD-10 Diagnosis Code(s) <span className="text-rose-400">*</span></Label>
                <ICD10Autocomplete
                  selectedCodes={selectedIcd10}
                  onAdd={(item) => setSelectedIcd10(prev => [...prev, item])}
                  onRemove={(code) => setSelectedIcd10(prev => prev.filter(c => c.code !== code))}
                />
                <p className="text-[11px] text-slate-400 mt-1.5">Type a code or diagnosis name. First code is primary.</p>
              </div>

              <div data-tour="drug-field">
                <Label>Denied Product / Drug <span className="text-rose-400">*</span></Label>
                <DrugAutocomplete value={deniedProduct} onChange={setDeniedProduct} icd10Codes={icd10Codes} />
              </div>

              <div data-tour="denial-field">
                <Label>Denial Reason <span className="text-rose-400">*</span></Label>
                <select className={InputClass} value={denialReason} onChange={e => setDenialReason(e.target.value)}>
                  <option value="">Select denial reason...</option>
                  {DENIAL_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Medical Policy Panel */}
          {(medicalPolicy?.found || policyLoading) && (
            <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-emerald-100 flex items-center gap-2.5 bg-emerald-50/50">
                <span className="w-6 h-6 rounded-md bg-emerald-500 text-white text-[11px] flex items-center justify-center font-bold">P</span>
                <span className="font-semibold text-emerald-700 text-sm">Payer Medical Policy</span>
                {policyLoading && <div className="w-3.5 h-3.5 border-2 border-emerald-200 border-t-emerald-500 rounded-full animate-spin ml-1" />}
              </div>

              {medicalPolicy?.found && (
                <div className="p-5 space-y-4">
                  <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-100">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-bold text-slate-700">{medicalPolicy.carrier_name}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal-500 text-white font-bold">{medicalPolicy.policy_number}</span>
                    </div>
                    <p className="text-[13px] text-slate-600 font-semibold">{medicalPolicy.policy_title}</p>
                    <p className="text-[11px] text-slate-400 mt-1">Indication: {medicalPolicy.indication}</p>
                  </div>

                  {medicalPolicy.required_prior_treatments && medicalPolicy.required_prior_treatments.length > 0 && (
                    <div>
                      <p className="text-[11px] font-bold text-slate-500 mb-2 uppercase tracking-wider">Required Prior Treatments</p>
                      <div className="space-y-1.5">
                        {medicalPolicy.required_prior_treatments.map((tx, i) => (
                          <div key={i} className="flex items-center gap-2.5 text-xs text-slate-600 bg-teal-50/50 rounded-lg px-3 py-2 border border-teal-100/60">
                            <span className="text-teal-500 font-bold flex-shrink-0">&#10003;</span>
                            <span className="font-medium">{tx}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {medicalPolicy.required_documentation && medicalPolicy.required_documentation.length > 0 && (
                    <div>
                      <p className="text-[11px] font-bold text-slate-500 mb-2 uppercase tracking-wider">Required Documentation</p>
                      <div className="space-y-1.5">
                        {medicalPolicy.required_documentation.map((doc, i) => (
                          <div key={i} className="flex items-center gap-2.5 text-xs text-slate-600 bg-amber-50/50 rounded-lg px-3 py-2 border border-amber-100/60">
                            <span className="w-3.5 h-3.5 rounded border-2 border-amber-300 flex-shrink-0 inline-block" />
                            <span className="font-medium">{doc}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-4 text-[11px] text-slate-400 pt-3 border-t border-slate-100">
                    {medicalPolicy.approval_duration && <span>&#9202; {medicalPolicy.approval_duration}</span>}
                    {medicalPolicy.quantity_limit && <span>&#8478; {medicalPolicy.quantity_limit}</span>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Section 3: Enrichment */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm dark:bg-slate-800 dark:border-slate-700">
            <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2.5 rounded-t-2xl">
              <span className="w-6 h-6 rounded-md bg-violet-500 text-white text-[11px] flex items-center justify-center font-bold">3</span>
              <span className="font-semibold text-slate-700 dark:text-slate-200 text-sm">Enrichment</span>
              <span className="text-[10px] bg-slate-100 text-slate-400 rounded-full px-2 py-0.5 font-semibold uppercase tracking-wider">Optional</span>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label>Prior Treatments Attempted</Label>
                <PriorTreatments
                  selectedTreatments={selectedPriorTx}
                  onAdd={(name) => setSelectedPriorTx(prev => [...prev, name])}
                  onRemove={(name) => setSelectedPriorTx(prev => prev.filter(t => t !== name))}
                  deniedProduct={deniedProduct}
                  icd10Codes={icd10Codes}
                  policyRequiredTreatments={medicalPolicy?.found ? medicalPolicy.required_prior_treatments : undefined}
                />
                <p className="text-[11px] text-slate-400 mt-1.5">Strengthens the appeal with treatment history.</p>
              </div>

              <div className="space-y-3 pt-1">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input type="checkbox" checked={specialist} onChange={e => setSpecialist(e.target.checked)}
                    className="w-4 h-4 accent-teal-500 rounded cursor-pointer" />
                  <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">Specialist involved in care</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input type="checkbox" checked={prevApproved} onChange={e => setPrevApproved(e.target.checked)}
                    className="w-4 h-4 accent-teal-500 rounded cursor-pointer" />
                  <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">Previously approved by a payer</span>
                </label>
              </div>

              {prevApproved && (
                <div className="space-y-3">
                  <div>
                    <Label>Which payer approved it?</Label>
                    <input className={InputClass} value={prevPayer} onChange={e => setPrevPayer(e.target.value)}
                      placeholder="e.g. Prior commercial plan" />
                  </div>

                  {/* Continuation of Care Law Reference */}
                  {continuityLoading && (
                    <div className="flex items-center gap-2 text-xs text-slate-400 py-2">
                      <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" /><path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="opacity-75" /></svg>
                      Looking up continuation of care laws...
                    </div>
                  )}
                  {continuityLaw && continuityLaw.found && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3.5 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        <span className="text-xs font-semibold text-blue-800">
                          {continuityLaw.state_name} Continuation of Care Law
                        </span>
                      </div>
                      <p className="text-xs text-blue-700 font-mono">{continuityLaw.statute} — {continuityLaw.title}</p>
                      <p className="text-xs text-blue-600">{continuityLaw.summary}</p>
                      {continuityLaw.transition_period && (
                        <p className="text-xs text-blue-500">
                          <span className="font-medium">Transition period:</span> {continuityLaw.transition_period}
                        </p>
                      )}
                      <p className="text-[10px] text-blue-400 mt-1">This statute will be referenced in the generated letter.</p>
                    </div>
                  )}
                  {continuityLaw && !continuityLaw.found && (
                    <p className="text-[11px] text-slate-400 italic">{continuityLaw.message || 'No continuation of care statute found for this state.'}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Generate button */}
          <button
            data-tour="generate-button"
            onClick={(trialExpired || monthlyLimitReached) ? undefined : handleGenerate}
            disabled={trialExpired || monthlyLimitReached || !canSubmit || loading}
            className={`w-full py-3.5 px-6 rounded-2xl font-semibold text-sm transition-all duration-200
              ${(trialExpired || monthlyLimitReached)
                ? 'bg-red-100 text-red-500 cursor-not-allowed border border-red-200'
                : canSubmit && !loading
                  ? 'bg-teal-500 hover:bg-teal-600 text-white cursor-pointer shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40 active:scale-[0.99]'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
              }`}
          >
            {trialExpired ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
                Free Trial Ended — Add Payment to Continue
              </span>
            ) : monthlyLimitReached ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
                Monthly Limit Reached — {userData.plan === 'starter' ? 'Upgrade or ' : ''}Wait Until Next Month
              </span>
            ) : loading ? (
              <span className="flex items-center justify-center gap-2.5">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block" />
                Generating LMN...
              </span>
            ) : (
              <span>
                Generate Letter of Medical Necessity
                {isInTrial && <span className="ml-2 text-teal-200 text-xs">({trialRemaining} free left)</span>}
                {userData.billingStarted && <span className="ml-2 text-teal-200 text-xs">({monthlyRemaining} left this month)</span>}
              </span>
            )}
          </button>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600">
              {error}
            </div>
          )}

          <button
            onClick={() => { setUploadCarrier(carrier); setUploadPlanName(''); setUploadFile(null); setUploadPolicyFile(null); setUploadResult(null); setShowUploadModal(true) }}
            className="w-full text-center text-xs text-slate-400 hover:text-teal-500 transition-colors mt-2 py-1"
          >
            Add a plan or upload a formulary
          </button>

          </>}
          {/* end formMode === 'single' */}
          </>}
          {/* end !demoMode */}
        </div>

        {/* ── Formulary Upload Modal ── */}
        {showUploadModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
            onClick={() => { setShowUploadModal(false); setUploadResult(null) }}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4"
              onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-slate-700">Add Plan & Upload Documents</h3>
                  <p className="text-[11px] text-slate-400 mt-0.5">Add a plan not in our database. Upload the formulary for accuracy.</p>
                </div>
                <button onClick={() => { setShowUploadModal(false); setUploadResult(null) }}
                  className="text-slate-400 hover:text-slate-600 text-lg font-bold">&times;</button>
              </div>

              {/* Carrier */}
              <div>
                <Label>Insurance Carrier <span className="text-rose-400">*</span></Label>
                <select className={InputClass} value={uploadCarrier} onChange={e => setUploadCarrier(e.target.value)}>
                  <option value="">Select carrier...</option>
                  {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Plan Name */}
              <div>
                <Label>Plan Name <span className="text-rose-400">*</span></Label>
                <input className={InputClass} value={uploadPlanName}
                  onChange={e => setUploadPlanName(e.target.value)}
                  placeholder="e.g. Aetna Gold PPO 2026, UHC Choice Plus" />
                <p className="text-[11px] text-slate-400 mt-1">Enter the exact plan name as shown on the member's insurance card</p>
              </div>

              {/* Formulary Upload */}
              <div className="border border-slate-200 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center text-[10px] font-bold">1</span>
                  <Label>Formulary / Drug List</Label>
                  {uploadFile && <span className="text-[10px] text-emerald-500 font-semibold ml-auto">✓ Selected</span>}
                </div>
                <input type="file" accept=".csv,.xlsx,.xls,.pdf"
                  className="block w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-600 hover:file:bg-teal-100 transition-all"
                  onChange={e => setUploadFile(e.target.files?.[0] ?? null)} />
                <p className="text-[10px] text-slate-400">The formulary must reference this plan or carrier to be accepted. CSV, Excel, or PDF.</p>
              </div>

              {/* Medical Policy Upload */}
              <div className="border border-slate-200 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[10px] font-bold">2</span>
                  <Label>Medical Policy <span className="text-slate-300 font-normal">(Optional)</span></Label>
                  {uploadPolicyFile && <span className="text-[10px] text-emerald-500 font-semibold ml-auto">✓ Selected</span>}
                </div>
                <input type="file" accept=".csv,.xlsx,.xls,.pdf"
                  className="block w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100 transition-all"
                  onChange={e => setUploadPolicyFile(e.target.files?.[0] ?? null)} />
                <p className="text-[10px] text-slate-400">Upload the payer's medical policy for this drug class if available. Must reference this carrier.</p>
              </div>

              {/* Validation / Result Messages */}
              {uploadResult && (
                <div className={`text-sm rounded-xl p-3.5 ${
                  uploadResult.success
                    ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                    : uploadResult.status === 'validation_failed'
                      ? 'bg-amber-50 border border-amber-200 text-amber-700'
                      : 'bg-red-50 border border-red-200 text-red-600'
                }`}>
                  {uploadResult.status === 'validation_failed' ? (
                    <>
                      <div className="flex items-start gap-2">
                        <span className="text-amber-500 text-lg leading-none">⚠</span>
                        <div>
                          <p className="font-semibold text-sm">Document Validation Failed</p>
                          <p className="text-xs mt-1">{uploadResult.message}</p>
                          {uploadResult.validation?.matched_terms && uploadResult.validation.matched_terms.length > 0 && (
                            <p className="text-[10px] mt-1.5 text-amber-500">
                              Found terms: {uploadResult.validation.matched_terms.join(', ')}
                            </p>
                          )}
                          <p className="text-[10px] mt-2 text-amber-600 font-medium">
                            Please upload a formulary that is specifically for this plan to ensure LMN accuracy.
                          </p>
                        </div>
                      </div>
                    </>
                  ) : uploadResult.success ? (
                    <>
                      <p className="font-semibold flex items-center gap-1.5">
                        <span className="text-emerald-500">✓</span> {uploadResult.message}
                      </p>
                      <div className="flex gap-3 mt-1.5 text-xs">
                        {uploadResult.formulary_uploaded && (
                          <span className="text-emerald-600">✓ Formulary uploaded</span>
                        )}
                        {uploadResult.policy_uploaded && (
                          <span className="text-emerald-600">✓ Medical policy uploaded</span>
                        )}
                        {!uploadResult.formulary_uploaded && !uploadResult.policy_uploaded && (
                          <span className="text-slate-400">No documents uploaded — you can add them later</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="font-semibold">{uploadResult.message || 'Upload failed'}</p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowUploadModal(false); setUploadResult(null) }}
                  className="flex-1 py-2.5 rounded-xl font-semibold text-sm bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={uploadResult?.success ? () => { setShowUploadModal(false); setUploadResult(null) } : handleAddPlanAndUpload}
                  disabled={(!uploadPlanName || !uploadCarrier || uploading) && !uploadResult?.success}
                  className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all
                    ${uploadResult?.success
                      ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/20'
                      : uploadPlanName && uploadCarrier && !uploading
                        ? 'bg-teal-500 hover:bg-teal-600 text-white shadow-lg shadow-teal-500/20'
                        : 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'}`}
                >
                  {uploading ? 'Adding Plan...' : uploadResult?.success ? 'Done' : 'Add Plan & Upload'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Doctor Manager Dropdown ── */}
        {showDoctorManager && (
          <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 bg-black/20 backdrop-blur-sm"
            onClick={() => setShowDoctorManager(false)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm border border-slate-200 overflow-hidden"
              onClick={e => e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-slate-800">Doctor Profiles</h3>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    {doctors.length} of {doctorLimit} doctors
                    <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-bold uppercase">
                      {getUserPlan() === 'professional_plus' ? 'Professional+' : getUserPlan()}
                    </span>
                  </p>
                </div>
                <button onClick={() => setShowDoctorManager(false)} className="text-slate-400 hover:text-slate-600 text-lg font-bold">&times;</button>
              </div>

              {/* Usage bar */}
              <div className="px-5 py-3 border-b border-slate-100">
                <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1.5">
                  <span>{doctors.length} doctor{doctors.length !== 1 ? 's' : ''} added</span>
                  <span>{doctorLimit - doctors.length} slot{doctorLimit - doctors.length !== 1 ? 's' : ''} available</span>
                </div>
                <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${doctors.length >= doctorLimit ? 'bg-amber-400' : 'bg-teal-400'}`}
                    style={{ width: `${Math.min(100, (doctors.length / doctorLimit) * 100)}%` }} />
                </div>
              </div>

              {doctors.length < doctorLimit ? (
                <button onClick={() => { setEditingDoctor(null); setShowDoctorModal(true); setShowDoctorManager(false) }}
                  className="w-full px-5 py-4 flex items-center gap-3 text-left hover:bg-teal-50/50 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-teal-100 text-teal-500 flex items-center justify-center text-xl font-bold flex-shrink-0">+</div>
                  <div>
                    <p className="text-sm font-semibold text-teal-700">Add a New Doctor</p>
                    <p className="text-[11px] text-slate-400">{doctorLimit - doctors.length} of {doctorLimit} slots remaining on your {getUserPlan()} plan</p>
                  </div>
                </button>
              ) : (
                <div className="px-5 py-4 bg-amber-50/50">
                  <p className="text-xs text-amber-600 font-semibold">Doctor limit reached ({doctorLimit} on {getUserPlan()} plan)</p>
                  <p className="text-[10px] text-amber-500 mt-0.5">
                    {getUserPlan() === 'starter' ? 'Upgrade to Professional for up to 8 doctors' : getUserPlan() === 'professional' || getUserPlan() === 'professional_plus' ? 'Contact us for Enterprise limits' : 'Contact us for enterprise limits'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Doctor Add/Edit Modal ── */}
        {showDoctorModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
            onClick={() => { setShowDoctorModal(false); setEditingDoctor(null) }}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6"
              onClick={e => e.stopPropagation()}>
              <DoctorForm
                doctor={editingDoctor}
                onSave={handleSaveDoctor}
                onCancel={() => { setShowDoctorModal(false); setEditingDoctor(null) }}
                isAtLimit={!editingDoctor && doctors.length >= doctorLimit}
                planName={getUserPlan()}
                limit={doctorLimit}
              />
            </div>
          </div>
        )}

        {/* ── Right Panel: Output ── */}
        <div className="flex-1 min-w-0">
          {!result && !loading && (
            <div className="h-full flex items-center justify-center">
              <div className="text-center space-y-5 max-w-sm">
                <div className="w-16 h-16 rounded-2xl bg-teal-50 border border-teal-100 flex items-center justify-center mx-auto text-teal-400 text-3xl font-light">
                  +
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-700">Ready to Generate</h2>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                    Complete the payer and clinical details on the left. Your Letter of Medical Necessity will appear here.
                  </p>
                </div>
                <div className="flex items-center justify-center gap-2 pt-1 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-3 py-1.5 text-[11px] text-slate-500 font-medium shadow-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-400" /> Formulary-Aware
                  </span>
                  <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-3 py-1.5 text-[11px] text-slate-500 font-medium shadow-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400" /> Policy-Referenced
                  </span>
                  <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-3 py-1.5 text-[11px] text-slate-500 font-medium shadow-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-violet-400" /> Guideline-Backed
                  </span>
                </div>
              </div>
            </div>
          )}

          {loading && (
            <div className="h-full flex items-center justify-center">
              <div className="text-center space-y-5">
                <div className="w-16 h-16 rounded-full border-4 border-slate-100 border-t-teal-500 animate-spin mx-auto" />
                <div>
                  <p className="text-teal-600 font-semibold">Generating your LMN</p>
                  <p className="text-sm text-slate-400 mt-1.5">Cross-referencing formulary, guidelines, and medical policy...</p>
                </div>
              </div>
            </div>
          )}

          {result && (
            <div id="letter-output">
              <LMNResult
                result={result}
                editedLetter={editedLetter}
                setEditedLetter={demoMode ? () => {} : setEditedLetter}
                deniedProduct={deniedProduct}
                carrier={carrier}
                selectedIcd10={selectedIcd10}
                selectedPlan={selectedPlan}
                whatsNextCollapsed={whatsNextCollapsed}
                setWhatsNextCollapsed={setWhatsNextCollapsed}
                onOpenAppeals={() => setShowAppealsDashboard(true)}
                onGenerateP2P={handleGenerateP2P}
                p2pLoading={p2pLoading}
                onSaveAsTemplate={() => { setSaveTemplateName(`${deniedProduct} - ${carrier}`); setShowSaveTemplateModal(true) }}
                saveTemplateSuccess={saveTemplateSuccess}
                demoMode={demoMode}
              />

              {/* Save as Template Modal */}
              {showSaveTemplateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
                  onClick={() => setShowSaveTemplateModal(false)}>
                  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-4"
                    onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                        <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                        Save as Template
                      </h3>
                      <button onClick={() => setShowSaveTemplateModal(false)}
                        className="text-slate-400 hover:text-slate-600 text-lg font-bold">&times;</button>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Template Name</label>
                      <input
                        className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                        value={saveTemplateName}
                        onChange={e => setSaveTemplateName(e.target.value)}
                        placeholder="e.g. Humira - Aetna Commercial"
                        autoFocus
                      />
                      <p className="text-[10px] text-slate-400 mt-1.5">Give this template a recognizable name for quick reuse.</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3 space-y-1">
                      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">What will be saved</p>
                      <p className="text-xs text-slate-600">{deniedProduct} &bull; {carrier} &bull; {planType}</p>
                      <p className="text-[11px] text-slate-400">{selectedIcd10.map(s => s.code).join(', ')}</p>
                    </div>
                    <button
                      onClick={handleSaveTemplate}
                      disabled={!saveTemplateName.trim() || saveTemplateLoading}
                      className="w-full py-2.5 rounded-xl bg-teal-500 hover:bg-teal-600 text-white font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saveTemplateLoading ? 'Saving...' : 'Save Template'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Appeals Dashboard ── */}
      {showAppealsDashboard && (
        <AppealsDashboard onClose={() => setShowAppealsDashboard(false)} plan={userData.plan} />
      )}

      {/* ── Payer Directory ── */}
      {showPayerDirectory && (
        <PayerDirectory
          onClose={() => setShowPayerDirectory(false)}
          doctorName={selectedDoctor?.name}
          doctorFax={selectedDoctor ? getActiveLocation(selectedDoctor, selectedLocationIdx).fax : undefined}
        />
      )}

      {/* ── P2P Prep Sheet Modal ── */}
      {showP2pModal && (
        <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col overflow-hidden">
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                </svg>
                <div>
                  <h2 className="text-white font-bold text-lg">Peer-to-Peer Review Prep Sheet</h2>
                  {p2pData && <p className="text-orange-100 text-xs">{p2pData.metadata.drug} | {p2pData.metadata.carrier} | {p2pData.metadata.date}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {p2pData && (
                  <>
                    <button onClick={handlePrintP2P} className="px-3 py-1.5 bg-white/20 text-white text-xs font-semibold rounded-lg hover:bg-white/30">Print</button>
                    <button onClick={handleDownloadP2P} className="px-3 py-1.5 bg-white/20 text-white text-xs font-semibold rounded-lg hover:bg-white/30">Download</button>
                  </>
                )}
                <button onClick={() => setShowP2pModal(false)} className="text-white/80 hover:text-white ml-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
            <div className="overflow-auto flex-1 p-6">
              {p2pLoading && (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin mb-4" />
                  <p className="text-slate-500 text-sm font-medium">Generating your P2P prep sheet...</p>
                  <p className="text-slate-400 text-xs mt-1">Building talking points and anticipated objections</p>
                </div>
              )}
              {p2pData && !p2pLoading && (
                <div className="space-y-3">
                  {/* Opening Statement */}
                  <div className="border border-emerald-200 rounded-xl overflow-hidden">
                    <button onClick={() => toggleP2pSection('opening')} className="w-full flex items-center justify-between px-4 py-3 bg-emerald-50 hover:bg-emerald-100 transition-colors text-left">
                      <span className="text-sm font-bold text-emerald-700 uppercase tracking-wide">Your Opening Statement (30 sec)</span>
                      <svg className={`w-4 h-4 text-emerald-500 transition-transform ${p2pExpandedSections.opening ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {p2pExpandedSections.opening && <div className="px-4 py-3 bg-white"><p className="text-sm text-emerald-800 italic leading-relaxed">"{p2pData.opening_statement}"</p></div>}
                  </div>
                  {/* Talking Points */}
                  <div className="border border-teal-200 rounded-xl overflow-hidden">
                    <button onClick={() => toggleP2pSection('talking_points')} className="w-full flex items-center justify-between px-4 py-3 bg-teal-50 hover:bg-teal-100 transition-colors text-left">
                      <span className="text-sm font-bold text-teal-700 uppercase tracking-wide">Key Talking Points ({p2pData.talking_points.length})</span>
                      <svg className={`w-4 h-4 text-teal-500 transition-transform ${p2pExpandedSections.talking_points ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {p2pExpandedSections.talking_points && (
                      <div className="px-4 py-3 bg-white space-y-3">
                        {p2pData.talking_points.map((tp, i) => (
                          <div key={i} className="border-l-3 border-teal-400 pl-3">
                            <p className="text-sm font-bold text-teal-700">{i + 1}. {tp.point}</p>
                            <p className="text-xs text-slate-600 mt-1 leading-relaxed">{tp.detail}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Objections & Responses */}
                  <div className="border border-red-200 rounded-xl overflow-hidden">
                    <button onClick={() => toggleP2pSection('objections')} className="w-full flex items-center justify-between px-4 py-3 bg-red-50 hover:bg-red-100 transition-colors text-left">
                      <span className="text-sm font-bold text-red-700 uppercase tracking-wide">Anticipated Objections &amp; Responses ({p2pData.objections_responses.length})</span>
                      <svg className={`w-4 h-4 text-red-500 transition-transform ${p2pExpandedSections.objections ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {p2pExpandedSections.objections && (
                      <div className="px-4 py-3 bg-white space-y-3">
                        {p2pData.objections_responses.map((obj, i) => (
                          <div key={i} className="rounded-lg border border-slate-200 overflow-hidden">
                            <div className="bg-red-50 px-3 py-2"><p className="text-xs"><span className="font-bold text-red-600">IF THEY SAY:</span> <span className="text-red-800">"{obj.objection}"</span></p></div>
                            <div className="bg-emerald-50 px-3 py-2"><p className="text-xs"><span className="font-bold text-emerald-600">RESPOND WITH:</span> <span className="text-emerald-800">"{obj.response}"</span></p></div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Clinical Data */}
                  <div className="border border-blue-200 rounded-xl overflow-hidden">
                    <button onClick={() => toggleP2pSection('clinical')} className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 hover:bg-blue-100 transition-colors text-left">
                      <span className="text-sm font-bold text-blue-700 uppercase tracking-wide">Clinical Data to Reference</span>
                      <svg className={`w-4 h-4 text-blue-500 transition-transform ${p2pExpandedSections.clinical ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {p2pExpandedSections.clinical && (
                      <div className="px-4 py-3 bg-white space-y-2">
                        <p className="text-xs"><span className="font-semibold text-blue-700">FDA Approval:</span> <span className="text-slate-600">{p2pData.clinical_data.fda_approval}</span></p>
                        {p2pData.clinical_data.key_trial && <p className="text-xs"><span className="font-semibold text-blue-700">Key Trial:</span> <span className="text-slate-600">{p2pData.clinical_data.key_trial}</span></p>}
                        {p2pData.clinical_data.guidelines.map((g, i) => <p key={i} className="text-xs"><span className="font-semibold text-blue-700">Guideline:</span> <span className="text-slate-600">{g}</span></p>)}
                        <p className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2"><span className="font-semibold text-amber-700">Patient Metrics:</span> <span className="text-amber-600">{p2pData.clinical_data.patient_metrics}</span></p>
                      </div>
                    )}
                  </div>
                  {/* Carrier Tips */}
                  <div className="border border-amber-200 rounded-xl overflow-hidden">
                    <button onClick={() => toggleP2pSection('carrier_tips')} className="w-full flex items-center justify-between px-4 py-3 bg-amber-50 hover:bg-amber-100 transition-colors text-left">
                      <span className="text-sm font-bold text-amber-700 uppercase tracking-wide">Carrier-Specific Tips</span>
                      <svg className={`w-4 h-4 text-amber-500 transition-transform ${p2pExpandedSections.carrier_tips ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {p2pExpandedSections.carrier_tips && (
                      <div className="px-4 py-3 bg-white">
                        <ul className="space-y-2">{p2pData.carrier_tips.map((tip, i) => <li key={i} className="text-xs text-amber-800 leading-relaxed flex items-start gap-2"><span className="text-amber-500 mt-0.5 font-bold flex-shrink-0">&bull;</span><span>{tip}</span></li>)}</ul>
                      </div>
                    )}
                  </div>
                  {/* Closing Statement */}
                  <div className="border border-emerald-200 rounded-xl overflow-hidden">
                    <button onClick={() => toggleP2pSection('closing')} className="w-full flex items-center justify-between px-4 py-3 bg-emerald-50 hover:bg-emerald-100 transition-colors text-left">
                      <span className="text-sm font-bold text-emerald-700 uppercase tracking-wide">Closing Statement</span>
                      <svg className={`w-4 h-4 text-emerald-500 transition-transform ${p2pExpandedSections.closing ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {p2pExpandedSections.closing && <div className="px-4 py-3 bg-white"><p className="text-sm text-emerald-800 italic leading-relaxed">"{p2pData.closing_statement}"</p></div>}
                  </div>
                  {/* If Unsuccessful */}
                  <div className="border-2 border-orange-300 rounded-xl overflow-hidden">
                    <button onClick={() => toggleP2pSection('unsuccessful')} className="w-full flex items-center justify-between px-4 py-3 bg-orange-50 hover:bg-orange-100 transition-colors text-left">
                      <span className="text-sm font-bold text-orange-700 uppercase tracking-wide">If the Call Doesn't Go Well</span>
                      <svg className={`w-4 h-4 text-orange-500 transition-transform ${p2pExpandedSections.unsuccessful ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {p2pExpandedSections.unsuccessful && (
                      <div className="px-4 py-3 bg-white">
                        <ul className="space-y-2">{p2pData.if_unsuccessful.map((item, i) => <li key={i} className="text-xs text-orange-800 leading-relaxed flex items-start gap-2"><span className="text-orange-500 mt-0.5 font-bold flex-shrink-0">{i + 1}.</span><span>{item}</span></li>)}</ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Account Settings Modal ── */}
      {showAccountSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className={`bg-white rounded-2xl shadow-2xl w-full mx-4 overflow-hidden transition-all ${accountTab === 'branding' ? 'max-w-2xl' : 'max-w-lg'}`}>
            <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm">
                  {userData.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                </div>
                <div>
                  <h2 className="text-white font-bold text-lg">Account Settings</h2>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-slate-300 text-xs">{userData.email}</p>
                    <span className={`inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      userData.plan === 'enterprise' ? 'bg-violet-400/20 text-violet-200 border border-violet-400/30'
                      : userData.plan === 'professional_plus' ? 'bg-indigo-400/20 text-indigo-200 border border-indigo-400/30'
                      : userData.plan === 'professional' ? 'bg-blue-400/20 text-blue-200 border border-blue-400/30'
                      : 'bg-slate-400/20 text-slate-300 border border-slate-400/30'
                    }`}>
                      Plan: {userData.plan === 'enterprise' ? 'Enterprise' : userData.plan === 'professional_plus' ? 'Professional+' : userData.plan === 'professional' ? 'Professional' : 'Starter'}
                    </span>
                  </div>
                </div>
              </div>
              <button onClick={() => setShowAccountSettings(false)} className="text-white/70 hover:text-white transition-colors">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-slate-200">
              <button onClick={() => setAccountTab('profile')}
                className={`flex-1 py-3 text-sm font-semibold text-center transition-colors ${accountTab === 'profile' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-400 hover:text-slate-600'}`}>
                Profile
              </button>
              <button onClick={() => setAccountTab('password')}
                className={`flex-1 py-3 text-sm font-semibold text-center transition-colors ${accountTab === 'password' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-400 hover:text-slate-600'}`}>
                Password
              </button>
              <button onClick={() => setAccountTab('subscription')}
                className={`flex-1 py-3 text-sm font-semibold text-center transition-colors ${accountTab === 'subscription' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-400 hover:text-slate-600'}`}>
                Subscription
              </button>
              <button onClick={() => { setAccountTab('team'); loadTeamData() }}
                className={`flex-1 py-3 text-sm font-semibold text-center transition-colors ${accountTab === 'team' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-400 hover:text-slate-600'}`}>
                Team
              </button>
              {userData.plan === 'enterprise' && (
                <button onClick={() => { setAccountTab('branding'); loadBrandingData() }}
                  className={`flex-1 py-3 text-sm font-semibold text-center transition-colors ${accountTab === 'branding' ? 'text-violet-600 border-b-2 border-violet-600' : 'text-slate-400 hover:text-slate-600'}`}>
                  Branding
                </button>
              )}
            </div>

            {accountTab === 'profile' && (
              <div className="p-6 space-y-4">
                {profileError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 font-medium">{profileError}</div>
                )}
                {profileSuccess && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-600 font-medium">Profile updated successfully!</div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Full Name</label>
                  <input type="text" value={profileName} onChange={e => setProfileName(e.target.value)}
                    className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
                  <input type="email" value={userData.email} disabled
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50 text-slate-500 cursor-not-allowed" />
                </div>

                <div className="flex gap-3 pt-2">
                  <button onClick={() => setShowAccountSettings(false)}
                    className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-600 text-sm font-semibold rounded-xl hover:bg-slate-50 transition-colors">
                    Close
                  </button>
                  <button onClick={handleSaveProfile}
                    disabled={profileSaving || !profileName.trim() || profileName.trim() === userData.name}
                    className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    {profileSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            )}

            {accountTab === 'subscription' && (
              <div className="p-6 space-y-4">
                {/* Payment failed warning */}
                {userData.paymentFailed && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                    <div className="flex items-center gap-2 mb-1">
                      <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01" />
                      </svg>
                      <span className="text-sm font-semibold text-red-700">Payment Failed</span>
                    </div>
                    <p className="text-xs text-red-600 mb-2">Your last payment was unsuccessful. Update your payment method to keep your subscription active.</p>
                    <div className="flex gap-2">
                      <button onClick={handleRetryPayment} disabled={billingLoading}
                        className="px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50">
                        {billingLoading ? 'Retrying...' : 'Retry Payment'}
                      </button>
                      <button onClick={handleManageBilling}
                        className="px-3 py-1.5 bg-white border border-red-300 text-red-600 text-xs font-semibold rounded-lg hover:bg-red-50">
                        Update Card
                      </button>
                    </div>
                  </div>
                )}

                {/* Plan badge + status */}
                <div className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center text-xs font-bold px-2.5 py-1 rounded-full ${
                        userData.plan === 'enterprise' ? 'bg-violet-100 text-violet-700'
                        : userData.plan === 'professional_plus' ? 'bg-indigo-100 text-indigo-700'
                        : userData.plan === 'professional' ? 'bg-blue-100 text-blue-700'
                        : 'bg-slate-200 text-slate-600'
                      }`}>
                        {userData.plan === 'enterprise' ? 'Enterprise' : userData.plan === 'professional_plus' ? 'Professional+' : userData.plan === 'professional' ? 'Professional' : 'Starter'}
                      </span>
                      {userData.cancelAtPeriodEnd && (
                        <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Cancelling</span>
                      )}
                    </div>
                    <button onClick={() => { setShowAccountSettings(false); setShowBillingModal(true); setBillingError(null); setBillingSuccess(null) }}
                      className="text-xs text-blue-600 font-semibold hover:text-blue-700">
                      {userData.billingStarted ? 'Change Plan' : 'Upgrade'}
                    </button>
                  </div>
                  <div className="text-sm text-slate-600">
                    {userData.billingStarted
                      ? <>{monthlyRemaining} of {userData.monthlyLmnLimit} LMNs remaining this month{bonusRemaining > 0 && <span className="text-indigo-500"> (+ {bonusRemaining} bonus)</span>}</>
                      : isInTrial
                        ? <>{trialRemaining} of {userData.trialLmnLimit} free trial LMNs remaining</>
                        : <>Trial ended — upgrade to continue generating LMNs</>
                    }
                  </div>
                  {userData.billingStarted && (
                    <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                      <div className={`h-2 rounded-full transition-all ${monthlyRemaining <= 5 ? 'bg-red-500' : monthlyRemaining <= 15 ? 'bg-amber-500' : 'bg-teal-500'}`}
                        style={{ width: `${Math.max(2, ((totalMonthlyAvailable - monthlyRemaining) / totalMonthlyAvailable) * 100)}%` }} />
                    </div>
                  )}
                  {userData.cancelAtPeriodEnd && userData.subscriptionEndsAt && (
                    <p className="text-xs text-amber-600 mt-2">
                      Access ends {new Date(userData.subscriptionEndsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      {' — '}
                      <button onClick={handleReactivate} className="text-blue-600 font-semibold hover:text-blue-700">Keep subscription</button>
                    </p>
                  )}
                  {userData.currentPeriodEnd && userData.billingStarted && !userData.cancelAtPeriodEnd && (
                    <p className="text-[10px] text-slate-400 mt-2">
                      Next billing: {new Date(userData.currentPeriodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <p className="text-xs text-slate-400 font-medium">Total LMNs Generated</p>
                    <p className="text-lg font-bold text-slate-800">{userData.lmnCount}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <p className="text-xs text-slate-400 font-medium">This Month</p>
                    <p className="text-lg font-bold text-slate-800">{userData.monthlyLmnCount}/{userData.monthlyLmnLimit}</p>
                  </div>
                </div>

                {userData.billingStarted && (
                  <div className="pt-2 space-y-2">
                    <button onClick={() => { setShowAccountSettings(false); setShowBillingModal(true); loadInvoices() }}
                      className="w-full px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors">
                      Manage Billing & Invoices
                    </button>
                    <button onClick={handleManageBilling}
                      className="w-full px-4 py-2.5 bg-slate-100 text-slate-600 text-sm font-semibold rounded-xl hover:bg-slate-200 transition-colors">
                      Update Payment Method
                    </button>
                  </div>
                )}
                {!userData.billingStarted && (
                  <div className="pt-2">
                    <button onClick={() => { setShowAccountSettings(false); setShowBillingModal(true) }}
                      className="w-full px-4 py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-xl hover:bg-teal-700 transition-colors">
                      Upgrade Plan
                    </button>
                  </div>
                )}
              </div>
            )}

            {accountTab === 'password' && (
              <div className="p-6 space-y-4">
                {passwordError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 font-medium">{passwordError}</div>
                )}
                {passwordSuccess && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-600 font-medium">Password changed successfully!</div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Current Password</label>
                  <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">New Password</label>
                  <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Confirm New Password</label>
                  <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>

                <div className="flex gap-3 pt-2">
                  <button onClick={() => setShowAccountSettings(false)}
                    className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-600 text-sm font-semibold rounded-xl hover:bg-slate-50 transition-colors">
                    Close
                  </button>
                  <button onClick={handleChangePassword}
                    disabled={passwordSaving || !currentPassword || !newPassword || !confirmPassword}
                    className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    {passwordSaving ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </div>
            )}

            {accountTab === 'team' && (
              <div className="p-6 space-y-4 max-h-[480px] overflow-y-auto">
                {teamError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 font-medium">{teamError}</div>
                )}
                {teamSuccess && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-600 font-medium">{teamSuccess}</div>
                )}

                {teamLoading ? (
                  <div className="space-y-3 py-2">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="p-3 bg-slate-50 rounded-xl">
                        <div className="skeleton h-4 w-40 mb-2" />
                        <div className="skeleton h-3 w-56" />
                      </div>
                    ))}
                  </div>
                ) : teamData ? (
                  <>
                    {/* Usage bar */}
                    <div className="bg-slate-50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-slate-500 uppercase">Team Users</span>
                        <span className="text-xs font-bold text-slate-700">{teamData.total_users} / {teamData.max_users}</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${teamData.total_users >= teamData.max_users ? 'bg-red-500' : 'bg-teal-500'}`}
                          style={{ width: `${Math.min((teamData.total_users / teamData.max_users) * 100, 100)}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1.5">
                        {teamData.plan === 'starter' ? 'Starter' : teamData.plan === 'professional' ? 'Professional' : 'Enterprise'} plan
                        — {teamData.max_users - teamData.total_users} slot{teamData.max_users - teamData.total_users !== 1 ? 's' : ''} remaining
                      </p>
                    </div>

                    {/* Owner */}
                    <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                      <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {teamData.owner.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{teamData.owner.name}</p>
                        <p className="text-xs text-slate-500 truncate">{teamData.owner.email}</p>
                      </div>
                      <span className="text-[10px] font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full uppercase">Owner</span>
                    </div>

                    {/* Members list */}
                    {teamData.members.length > 0 && (
                      <div className="space-y-2">
                        {teamData.members.map(member => (
                          <div key={member.id} className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl">
                            <div className="w-9 h-9 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                              {member.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-slate-800 truncate">{member.name}</p>
                              <p className="text-xs text-slate-500 truncate">{member.email}</p>
                            </div>
                            <button
                              onClick={() => handleRemoveTeamMember(member.id, member.name)}
                              className="text-xs text-red-500 hover:text-red-700 font-semibold transition-colors flex-shrink-0"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {teamData.members.length === 0 && (
                      <p className="text-sm text-slate-400 text-center py-2">No team members yet. Add your first one below.</p>
                    )}

                    {/* Add member form */}
                    {showAddMember ? (
                      <div className="bg-slate-50 rounded-xl p-4 space-y-3 border border-slate-200">
                        <h4 className="text-sm font-bold text-slate-700">Add Team Member</h4>
                        <div>
                          <label className="block text-xs font-semibold text-slate-500 mb-1">Name</label>
                          <input type="text" value={newMemberName} onChange={e => setNewMemberName(e.target.value)}
                            placeholder="Jane Smith"
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-500 mb-1">Email</label>
                          <input type="email" value={newMemberEmail} onChange={e => setNewMemberEmail(e.target.value)}
                            placeholder="jane@practice.com"
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-500 mb-1">Password</label>
                          <input type="password" value={newMemberPassword} onChange={e => setNewMemberPassword(e.target.value)}
                            placeholder="At least 8 characters"
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => { setShowAddMember(false); setNewMemberName(''); setNewMemberEmail(''); setNewMemberPassword('') }}
                            className="flex-1 px-3 py-2 text-sm font-semibold text-slate-600 border border-slate-300 rounded-lg hover:bg-white transition-colors">
                            Cancel
                          </button>
                          <button onClick={handleAddTeamMember}
                            disabled={teamSaving || !newMemberName.trim() || !newMemberEmail.trim() || newMemberPassword.length < 8}
                            className="flex-1 px-3 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            {teamSaving ? 'Adding...' : 'Add Member'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowAddMember(true)}
                        disabled={teamData.total_users >= teamData.max_users}
                        className="w-full py-2.5 border-2 border-dashed border-slate-300 text-sm font-semibold text-slate-500 rounded-xl hover:border-blue-400 hover:text-blue-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-300 disabled:hover:text-slate-500"
                      >
                        {teamData.total_users >= teamData.max_users
                          ? `Team limit reached (${teamData.max_users} users) — Upgrade to add more`
                          : '+ Add Team Member'}
                      </button>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">Unable to load team data.</p>
                )}
              </div>
            )}

            {/* ── Branding Tab (Enterprise only) ── */}
            {accountTab === 'branding' && userData.plan === 'enterprise' && (
              <BrandingSettings
                brandingData={brandingData}
                brandingLoading={brandingLoading}
                brandingSaving={brandingSaving}
                brandingSuccess={brandingSuccess}
                brandingError={brandingError}
                brandingPrimaryColor={brandingPrimaryColor}
                brandingAccentColor={brandingAccentColor}
                brandingDisplayName={brandingDisplayName}
                brandingFooterText={brandingFooterText}
                brandingEmailHeader={brandingEmailHeader}
                brandingHidePoweredBy={brandingHidePoweredBy}
                brandingLetterheadEnabled={brandingLetterheadEnabled}
                brandingLetterheadHtml={brandingLetterheadHtml}
                brandingLogoUploading={brandingLogoUploading}
                userName={userData.name}
                onPrimaryColorChange={setBrandingPrimaryColor}
                onAccentColorChange={setBrandingAccentColor}
                onDisplayNameChange={setBrandingDisplayName}
                onFooterTextChange={setBrandingFooterText}
                onEmailHeaderChange={setBrandingEmailHeader}
                onHidePoweredByToggle={() => setBrandingHidePoweredBy(!brandingHidePoweredBy)}
                onLetterheadEnabledToggle={() => setBrandingLetterheadEnabled(!brandingLetterheadEnabled)}
                onLetterheadHtmlChange={setBrandingLetterheadHtml}
                onResetColors={handleResetBrandingColors}
                onLogoUpload={handleBrandingLogoUpload}
                onLogoDelete={handleBrandingLogoDelete}
                onSave={handleSaveBranding}
                onClose={() => setShowAccountSettings(false)}
              />
            )}
          </div>
        </div>
      )}

      {/* ── Support Ticket Modal ── */}
      <SupportTicketModal
        show={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        supportSuccess={supportSuccess}
        supportError={supportError}
        supportSubmitting={supportSubmitting}
        supportCategory={supportCategory}
        onCategoryChange={setSupportCategory}
        supportSubject={supportSubject}
        onSubjectChange={setSupportSubject}
        supportMessage={supportMessage}
        onMessageChange={setSupportMessage}
        onSubmit={handleSubmitSupportTicket}
      />

      {/* ── First-Run Welcome Overlay ── */}
      <FirstRunWelcome
        show={showFirstRunWelcome && isFirstTimeUser}
        onDismiss={() => {
          setShowFirstRunWelcome(false)
          localStorage.setItem('checkup_onboarding_dismissed', 'true')
        }}
      />

      {/* ── Interactive Guided Tour (spotlight first-LMN walkthrough) ── */}
      {/* Only show AFTER the welcome overlay is dismissed to avoid overlap */}
      <GuidedTour
        steps={FIRST_LMN_TOUR}
        open={showOnboarding && !(showFirstRunWelcome && isFirstTimeUser)}
        onClose={() => { setShowOnboarding(false); localStorage.setItem('checkup_onboarding_done', 'true') }}
        onComplete={() => localStorage.setItem('checkup_onboarding_done', 'true')}
      />

      {/* ── Legacy Static Onboarding Modal (disabled in favor of GuidedTour) ── */}
      {false && showOnboarding && (
        <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-6 py-5 text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-xl">
                  {onboardingStep === 0 ? '👋' : onboardingStep === 1 ? '👨‍⚕️' : onboardingStep === 2 ? '📝' : onboardingStep === 3 ? '📊' : '🚀'}
                </div>
                <div>
                  <h3 className="font-bold text-lg">
                    {onboardingStep === 0 ? 'Welcome to RxCheckUp!' :
                     onboardingStep === 1 ? 'Set Up Your Doctor Profile' :
                     onboardingStep === 2 ? 'Generate Your First LMN' :
                     onboardingStep === 3 ? 'Track Your Appeals' :
                     "You're All Set!"}
                  </h3>
                  <p className="text-teal-100 text-xs">Step {onboardingStep + 1} of 5</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-5">
              {onboardingStep === 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600 leading-relaxed">RxCheckUp helps you generate clinically precise Letters of Medical Necessity in seconds. Here's a quick tour of the key features.</p>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <p className="text-xs text-amber-700"><span className="font-bold">Remember:</span> Never enter patient-identifiable information (names, dates of birth, medical record numbers) on this platform. Add patient details only after downloading your letter.</p>
                  </div>
                </div>
              )}
              {onboardingStep === 1 && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600 leading-relaxed">Start by adding your doctor profile using the button in the top navigation bar. Include your NPI, specialty, and practice information — this data auto-populates every letter you generate.</p>
                  <p className="text-sm text-slate-600 leading-relaxed">You can add multiple providers and switch between them for multi-doctor practices.</p>
                </div>
              )}
              {onboardingStep === 2 && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600 leading-relaxed">To generate an LMN, fill in the form on the left side of your dashboard:</p>
                  <ul className="text-sm text-slate-600 space-y-1.5 ml-4">
                    <li className="flex items-start gap-2"><span className="text-teal-500 font-bold">1.</span> Enter the denied medication name</li>
                    <li className="flex items-start gap-2"><span className="text-teal-500 font-bold">2.</span> Select the insurance carrier & plan type</li>
                    <li className="flex items-start gap-2"><span className="text-teal-500 font-bold">3.</span> Choose the denial reason</li>
                    <li className="flex items-start gap-2"><span className="text-teal-500 font-bold">4.</span> Add ICD-10 diagnosis codes</li>
                  </ul>
                  <p className="text-sm text-slate-600 leading-relaxed">Click <strong>"Generate LMN"</strong> and your letter appears in seconds with citations and a checklist.</p>
                </div>
              )}
              {onboardingStep === 3 && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600 leading-relaxed">Use the <strong>Appeals Tracker</strong> to monitor outcomes after submitting your letters. Mark whether each appeal was approved or denied.</p>
                  <p className="text-sm text-slate-600 leading-relaxed">When denied, provide the payer's feedback and our system <strong>learns from it</strong> to generate stronger letters next time for that carrier and drug.</p>
                </div>
              )}
              {onboardingStep === 4 && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600 leading-relaxed">You're ready to go! Here's what's available in your toolbar:</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-slate-50 rounded-lg p-2.5 text-center"><span className="block font-bold text-slate-700">Appeals</span><span className="text-slate-400">Track & search letters</span></div>
                    <div className="bg-slate-50 rounded-lg p-2.5 text-center"><span className="block font-bold text-slate-700">Payer Directory</span><span className="text-slate-400">Contacts & fax</span></div>
                    <div className="bg-slate-50 rounded-lg p-2.5 text-center"><span className="block font-bold text-slate-700">Help</span><span className="text-slate-400">Support tickets</span></div>
                    <div className="bg-slate-50 rounded-lg p-2.5 text-center"><span className="block font-bold text-slate-700">Settings</span><span className="text-slate-400">Profile & billing</span></div>
                  </div>
                </div>
              )}

              {/* Progress dots */}
              <div className="flex items-center justify-center gap-2 mt-5">
                {[0,1,2,3,4].map(i => (
                  <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === onboardingStep ? 'bg-teal-500' : 'bg-slate-200'}`} />
                ))}
              </div>
            </div>
            <div className="px-6 pb-5 flex gap-3">
              <button
                onClick={() => { setShowOnboarding(false); localStorage.setItem('checkup_onboarding_done', 'true') }}
                className="flex-1 py-2.5 text-sm font-semibold text-slate-500 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
              >
                Skip Tour
              </button>
              <button
                onClick={() => {
                  if (onboardingStep < 4) {
                    setOnboardingStep(onboardingStep + 1)
                  } else {
                    setShowOnboarding(false)
                    localStorage.setItem('checkup_onboarding_done', 'true')
                  }
                }}
                className="flex-1 py-2.5 text-sm font-semibold text-white bg-teal-500 rounded-xl hover:bg-teal-600 transition-colors"
              >
                {onboardingStep < 4 ? 'Next' : 'Get Started'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts */}
      <KeyboardShortcuts
        showModal={showShortcutsModal}
        onClose={() => setShowShortcutsModal(false)}
        onOpen={() => setShowShortcutsModal(true)}
      />

      {/* Footer */}
      <div className="text-center text-xs text-slate-400 py-4 border-t border-slate-100">
        <a href="/privacy" className="hover:text-slate-600 transition-colors">Privacy Policy</a>
        <span className="mx-2">&middot;</span>
        <a href="/terms" className="hover:text-slate-600 transition-colors">Terms of Service</a>
        <span className="mx-2">&middot;</span>
        <span>&copy; 2026 RxCheckUp LLC</span>
      </div>

    </div>
  )
}
